// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 4 miest
// FakturyView — Faktúry hlavná karta so 4 podkartami (Mirko 2026-05-21)
//   1. Vystavené FA   — invoices (MKsoft) + outgoing_invoices (Matrix ručné)
//   2. Došlé FA       — incoming_invoices doc_type='invoice'
//   3. Pokladničné bločky — incoming_invoices doc_type='blocok'
//   4. Pokladňa       — pokladna (hotovostné pohyby)
// Master-detail (sek. 56), chip-style podkarty (sek. 57), font/farba podľa theme.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { strazKlik } from '../hooks/unsavedGuard.js'   // straz_v1 (ústava 53.1)
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext.jsx'
import { useLock } from '../hooks/useLock.js'
import { qrUhrada } from '../utils/qrPlatba.js'   // qr_lokalne_v1
import { smieSpracovatBlocky, ZARADI_BACK_OFFICE } from '../utils/blocky.js'   // BLOCKY_SPRACUJE_V1 (ústava 135.2)
import { fetchAuth, fetchAuthJSON } from '../auth/fetchAuth.js'
import { useBreadcrumb } from '../components/Layout.jsx'
import { useToast } from '../components/Toast.jsx'
import { useShortcuts } from '../hooks/useShortcuts.js'
import { useListKeyboard } from '../hooks/useListKeyboard.js'
import { useSortable } from '../hooks/useSortable.js'
import { useDirtyState } from '../hooks/useDirtyState.js'
import { usePinGate } from '../hooks/usePinGate.jsx'
import { Btn, BtnAdd, BtnNav, BtnGhost } from '../components/Btn.jsx'
import { Input, NumberInput, Textarea } from '../components/Input.jsx'
import { Modal } from '../components/Modal.jsx'
import { COLORS, TYPO, SPACE, RADIUS, SK, todayLocal, hladaj } from '../theme.js'
import { KpiCard, SearchableSelect } from '../components/Ui.jsx'
import { FaDetailPane, REZIA_CATS_FD, ZAKAZKA_CATS_FD } from './FaktDetail.jsx'
import PriraditPlatbu from '../components/PriraditPlatbu.jsx'
import SyncBadge from '../components/SyncBadge.jsx'
import RowMenu from '../components/RowMenu.jsx'
import HelpHint from '../components/HelpHint.jsx'
import { useConfirm } from '../components/ConfirmDialog.jsx'
import UctovanieExportPane from './UctovanieExportPane.jsx'

// 4 podkarty
const UCT_ROLES = ['admin', 'office_manager', 'accountant']
// jedna_obrazovka_v1 (18. 10. 2026, ústava 0.J): došlé FA, bločky, Treba uhradiť a Na zaúčtovanie žijú len v Došlých FA (/dosle-v3).
// Tu ostávajú Vystavené FA; staré záložky presmerúva PRESMEROVANIE nižšie (odkazy z NEO, Miezd, záložiek v prehliadači).
const SUBTABS = [
  { key: 'vystavene', label: 'Vystavené FA' },
]
const PRESMEROVANIE = { dosle: 'fa', incoming: 'fa', blocky: 'blocky', treba_uhradit: 'treba', uctovanie: 'uctovanie', pokladna: 'fa' }

// Typy pre Vystavené
const TYPES_OUT = [
  { key: 'all',    label: 'Všetky', color: COLORS.muted },
  { key: 'FVSP',   label: 'FVSP',   color: COLORS.add,    hint: 'Stavebné práce' },
  // FVO/FVE presunuté do samostatnej karty Predajňa & Eshop (/predajna) — Mirko 2026-07-06, ústava 45
  { key: 'ZA',     label: 'ZA',     color: COLORS.total,  hint: 'Záloha' },
  { key: 'DN',     label: 'DN',     color: COLORS.remove, hint: 'Dobropis' },
  { key: 'manual', label: 'Ručné',  color: COLORS.warn,   hint: 'Vystavené ručne v Matrix' }
]

const PAY_STATUSES = [
  { key: 'all',     label: 'Všetky',        color: COLORS.muted },
  { key: 'unpaid',  label: 'Neuhradené',    color: COLORS.remove, icon: '' },
  { key: 'partial', label: 'Čiastočne',     color: COLORS.total,  icon: '' },
  { key: 'paid',    label: 'Uhradené',      color: COLORS.add,    icon: '✓' },
  { key: 'overdue', label: 'Po splatnosti', color: COLORS.remove }
]

const MONTH_NAMES_FV = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'Máj', 'Jún', 'Júl', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
const monthLabelFV = (ym) => { const [y, m] = ym.split('-'); return (MONTH_NAMES_FV[parseInt(m, 10)] || m) + ' ' + y }

function normalizeMksoft(i) {
  return {
    id: 'M:' + i.id, realId: i.id, source: 'mksoft',
    cislo: i.cislo || '', typ: i.typ || '',
    project_id: i.project_id || null, customer_id: i.customer_id || null,
    customer_name: i.customer_name || '', supplier_name: '',
    date_issued: i.date_issued || '', date_due: i.date_due || '', date_paid: null,
    amount_total: i.amount_total || 0, amount_paid: i.amount_paid || 0,
    vs: i.vs || '', mksoft_cislo: i.mksoft_cislo || '',
    status: i.status || '', note: i.note || '',
    vat_lines: '', vat_rate: i.vat_rate1 || 23,
    amount_excl_vat: i.amount_excl_vat || 0, amount_vat: i.amount_vat || 0
  }
}
function normalizeOutgoing(o) {
  // faktury_raz_v1: /api/outgoing-invoices vracia OBA zdroje (UNION s tabuľkou invoices) a rozlišuje ich
  // poľom from_mksoft. Táto funkcia ho doteraz zahodila a každú faktúru označila za „matrix/manual" —
  // preto filter „Ručné" vracal všetkých 326 dokladov za 4 354 285,99 €, hoci ručných je nula.
  const zMksoftu = !!o.from_mksoft
  return {
    id: (zMksoftu ? 'M:' : 'O:') + o.id, realId: o.id, source: zMksoftu ? 'mksoft' : 'matrix',
    from_mksoft: zMksoftu ? 1 : 0,
    cislo: o.invoice_number || '', typ: zMksoftu ? (o.typ || '') : 'manual',
    project_id: o.project_id || null, customer_id: o.customer_id || null,
    customer_name: o.customer_name || '', supplier_name: '',
    date_issued: o.date_issued || '', date_due: o.date_due || '', date_paid: o.date_paid || null,
    amount_total: o.amount_total || 0, amount_paid: o.amount_paid || 0,
    vs: o.vs || '', mksoft_cislo: o.mksoft_cislo || '',
    status: o.status || '', note: o.notes || '',
    vat_lines: '', vat_rate: o.vat_rate || 23,
    amount_excl_vat: o.amount_excl_vat || 0, amount_vat: o.amount_vat || 0
  }
}
export function normalizeIncoming(i) {
  const isBlocok = i.doc_type === 'blocok'
  return {
    id: 'I:' + i.id, realId: i.id, source: 'incoming',
    cislo: i.invoice_number || '', typ: isBlocok ? 'BLOCEK' : 'FA',
    docType: i.doc_type || 'invoice',
    project_id: i.project_id || null, customer_id: null,
    customer_name: '', supplier_name: i.supplier_name || '', supplier_ico: i.supplier_ico || '',
    supplier_iban: i.supplier_iban || '',
    partner_id: i.partner_id || null,
    date_issued: i.date_issued || '', date_due: i.date_due || '', date_paid: i.payment_date || null,
    amount_total: i.amount_total || 0,
    amount_paid: i.payment_date ? (i.amount_total || 0) : 0,
    vs: i.variable_symbol || '', mksoft_cislo: '',
    invoice_kind: i.invoice_kind || '', cost_center: i.cost_center || '', delena: i.delena || 0,
    cost_category: i.cost_category || '',
    kind_confirmed: i.kind_confirmed != null ? i.kind_confirmed : 1,
    status: i.status || '', note: i.notes || '',
    na_uhradu: i.na_uhradu || 0, na_uhradu_at: i.na_uhradu_at || null,
    vat_lines: i.vat_lines || '', vat_rate: i.vat_rate || 0,
    amount_excl_vat: i.amount_ex_vat || 0, amount_vat: i.vat_amount || 0,
    neo_import: i.neo_import || 0,
    doc_file: i.doc_file || '',
  }
}

export function needsAssign(fa) {
  // Doklad čaká na určenie ak: nepotvrdený druh, prázdny druh,
  // nákladová bez strediska, alebo na zákazku bez priradenej zákazky.
  if (!fa) return false
  if (!fa.kind_confirmed) return true
  const kind = (fa.invoice_kind || '').toLowerCase()
  const center = (fa.cost_center || '').toLowerCase()
  if (!kind) return true
  if (kind === 'nakladova' && !center) return true
  if (center === 'zakazka' && !fa.project_id) return true
  return false
}

export function payStatus(fa) {
  if (fa.docType === 'blocok' || fa.typ === 'BLOCEK') return 'paid'  // bloček je vždy uhradený (hotovosť/karta na pokladni)
  const total = fa.amount_total || 0, paid = fa.amount_paid || 0
  if (total <= 0) return 'unpaid'
  if (paid >= total - 0.01) return 'paid'
  if (paid > 0.01) return 'partial'
  if (fa.date_due) {
    const due = new Date(fa.date_due)
    if (!isNaN(due) && due < new Date()) return 'overdue'
  }
  return 'unpaid'
}

const badge = (label, color, icon) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 3,
    padding: '1px 6px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold,
    color, background: 'transparent', border: `1px solid ${color}`,
    borderRadius: RADIUS.full, whiteSpace: 'nowrap'
  }}>{icon ? icon + ' ' : ''}{label}</span>
)

function buildGroupQr(g) {
  // qr_lokalne_v1: EPC QR z jedného miesta (utils/qrPlatba.js), kreslí sa lokálne — IBAN a suma neodchádzajú von
  const ref = (g.vs || '').replace(/\D/g, '') || '0'
  return qrUhrada({ iban: g.iban, suma: g.suma, prijemca: g.supplier, vs: ref, poznamka: 'Konsolidovana platba ' + ref })
}

// GroupQr — QR na úhradu konsolidovanej skupiny (rovnaký IBAN). EPC/SEPA ako v detaile FA (Mirko 9. 6. 2026: bysquare jeho banke nefungoval).
function GroupQr({ g, size = 150 }) {
  const url = buildGroupQr(g)
  const box = { width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: TYPO.sizes.xs, textAlign: 'center', background: COLORS.bgSoft, borderRadius: RADIUS.sm, padding: 8 }
  if (!url) return <div style={{ ...box, color: COLORS.warn }}>Bez QR<br />(chýba/neplatný SK IBAN)</div>
  return <img src={url} alt="QR na úhradu" style={{ width: size, height: size, background: '#fff', padding: 5, borderRadius: RADIUS.sm, border: `1px solid ${COLORS.line}` }} />
}

function NaUhraduModal({ onClose }) {
  const [data, setData] = useState(null)
  useEffect(() => { fetchAuth('/api/incoming-invoices/na-uhradu').then(r => r.ok ? r.json() : null).then(setData).catch(() => setData({ konsolidacie: [] })) }, [])
  const groups = (data && data.konsolidacie) || []
  return (
    <Modal open title="Treba uhradiť — konsolidované QR platby" onClose={onClose} width={760}
      footer={<BtnGhost onClick={onClose}>Zavrieť</BtnGhost>}>
      <div style={{ padding: SPACE.sm, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: RADIUS.sm, fontSize: TYPO.sizes.xs, color: '#1e40af', marginBottom: SPACE.md, lineHeight: 1.5 }}>
        Rovnaký príjemca (IBAN) = <b>jedna platba, jeden VS</b> (= prvá FA) → menej transakcií, nižšia transakčná daň (ústava 87.3). Naskenuj QR v mobilnom bankovníctve. V poznámke platby uveď čísla FA.
      </div>
      {!data ? <div style={{ color: COLORS.muted, padding: SPACE.lg }}>Načítavam…</div>
        : groups.length === 0 ? <div style={{ color: COLORS.muted, padding: SPACE.lg, textAlign: 'center' }}>Žiadne faktúry na úhradu.</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
            {groups.map((g, i) => {
              const qr = buildGroupQr(g)
              return (
                <div key={i} style={{ display: 'flex', gap: SPACE.md, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: SPACE.md, background: COLORS.bg }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.md }}>{g.supplier || '—'}</div>
                    <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontFamily: TYPO.mono, marginTop: 2 }}>IBAN: {g.iban || 'chýba'}</div>
                    <div style={{ marginTop: 6, fontSize: TYPO.sizes.sm }}>Suma: <b style={{ fontFamily: TYPO.mono, color: COLORS.total }}>{SK.eur(g.suma)}</b> · VS: <b style={{ fontFamily: TYPO.mono, color: COLORS.nav }}>{g.vs || '—'}</b></div>
                    <div style={{ marginTop: 4, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{g.pocet} faktúr: {(g.fa_cisla || []).join(', ')}</div>
                  </div>
                  <div style={{ flexShrink: 0, textAlign: 'center' }}>
                    <GroupQr g={g} size={150} />
                  </div>
                </div>
              )
            })}
          </div>}
    </Modal>
  )
}

// BANKMATCH_2026-07-05: rucne sparovanie prijatej platby z vypisu VUB na FA (ustava 12.10 „Oprav ciel")
function BankMatchModal({ fa, onClose, onDone }) {
  const toast = useToast()
  const [rows, setRows] = useState([])
  const [q, setQ] = useState('')
  const [sel, setSel] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [priradit, setPriradit] = useState(false)   // Saldo F1a-2: vybraná platba sa priradí v okne Priradiť
  const remaining = Math.max(0, (fa.amount_total || 0) - (fa.amount_paid || 0))
  useEffect(() => {
    setLoading(true)
    fetchAuth(`/api/bank/candidates?vs=${encodeURIComponent(fa.vs || fa.cislo || '')}&amount=${remaining}&q=${encodeURIComponent(q)}`)
      .then(r => r.ok ? r.json() : [])
      .then(d => { const a = Array.isArray(d) ? d : []; setRows(a); setSel(s => s || ((a[0] && (a[0]._score || 0) >= 200) ? a[0] : null)) })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [q])
  // Saldo F1a-2 (15. 9. 2026, Mirko: „nemôže byť iba potvrď, musí byť priradiť“): vybraná platba sa nepripíše priamo — otvorí okno Priradiť,
  // kde sa rozdelí na túto a ďalšie faktúry, zádržné alebo preplatok; faktúry z MKsoftu sa nemenia (17.1).
  const doPair = () => { if (sel) setPriradit(true) }
  const niceD = s => { const m = String(s || '').match(/(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}.${m[2]}.${m[1]}` : '\u2014' }
  const cell = { padding: '6px 8px', verticalAlign: 'middle' }
  return (<>
    <Modal open title={`Spárovať platbu z výpisu — FA ${fa.cislo || fa.realId}`}
      subtitle={`${fa.customer_name || ''} · zostáva ${SK.eur(remaining)}`} onClose={onClose} width="min(820px, 95vw)"
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACE.sm, width: '100%' }}>
          <BtnGhost onClick={onClose}>Zrušiť</BtnGhost>
          <Btn onClick={doPair} disabled={!sel}>Priradiť {sel ? SK.eur(sel.amount || 0) : 'platbu'}</Btn>
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
        <Input placeholder="Hľadať v pohyboch (platiteľ / VS / popis)…" value={q} onChange={v => setQ(v)} />
        <div style={{ maxHeight: 380, overflow: 'auto', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
            <thead>
              <tr style={{ background: COLORS.bgSoft }}>
                {['', 'Dátum', 'Suma', 'Platiteľ', 'VS', 'Popis'].map(h => (
                  <th key={h} style={{ ...cell, textAlign: 'left', fontSize: TYPO.sizes.xs, color: COLORS.muted, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: COLORS.muted }}>Načítavam…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: COLORS.muted }}>Žiadne nespárované prijaté platby.</td></tr>
              ) : rows.map(r => (
                <tr key={r.id} onClick={strazKlik(() => setSel(r))} style={{ cursor: 'pointer', borderBottom: `1px solid ${COLORS.line}`,
                  background: sel && sel.id === r.id ? '#dbeafe' : 'transparent' }}>
                  <td style={cell}><input type="radio" checked={!!sel && sel.id === r.id} onChange={() => setSel(r)} /></td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>{(r._score || 0) >= 200 ? '' : ''}{niceD(r.booking_date)}</td>
                  <td style={{ ...cell, textAlign: 'right', fontFamily: TYPO.mono, fontWeight: 700 }}>{SK.eur(r.amount || 0)}</td>
                  <td style={{ ...cell, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.cp_name || '\u2014'}</td>
                  <td style={{ ...cell, fontFamily: TYPO.mono }}>{r.vs || '\u2014'}</td>
                  <td style={{ ...cell, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: COLORS.muted }} title={r.description || ''}>{r.description || '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
          Zoradené podľa zhody: VS → suma → dátum. = návrh Matrixu (najbližšia zhoda, predvolený). Vybranú platbu otvorí okno Priradiť: rozdelíš ju na túto a ďalšie faktúry, zádržné alebo preplatok. Faktúry z MKsoftu sa nemenia, zapíše sa saldo v Matrixe.
        </div>
      </div>
    </Modal>
    {priradit && sel && <PriraditPlatbu txId={sel.id} predvolenyDoklad={{ doklad_typ: fa.source === 'mksoft' ? 'fa_mksoft' : 'fa_vystavena', doklad_id: String(fa.realId), cislo: fa.cislo || '' }} onClose={() => setPriradit(false)} onSaved={() => { setPriradit(false); onDone && onDone(true); onClose() }} />}
  </>)
}

export function ReclassModal({ fa, onClose, onDone }) {
  const toast = useToast()
  const [projects, setProjects] = useState([])
  const [proj, setProj] = useState(fa.project_id || '')
  const [docType, setDocType] = useState('Podpísaný súpis prác')
  const [busy, setBusy] = useState(false)
  useEffect(() => { fetchAuth('/api/projects?scope=all').then(r => r.ok ? r.json() : []).then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {}) }, [])
  const submit = async () => {
    if (!proj) { toast.error('Vyber zákazku'); return }
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/incoming-invoices/${fa.realId}/to-project-doc`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ project_id: proj, doc_type: docType }) })
      const j = await r.json(); if (!r.ok) throw new Error(j.error || 'Zlyhalo')
      const pName = (projects.find(p => p.id === proj) || {}).name || proj
      onClose(); onDone && onDone()
      toast.undo(`Presunuté do dokumentov zákazky „${pName}“`, async () => {
        await fetchAuth(`/api/incoming-invoices/${fa.realId}/undo-to-project-doc`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ project_doc_id: j.project_doc_id }) }).catch(() => {})
        onDone && onDone()
      })
    } catch (e) { toast.error('Presun zlyhal: ' + e.message); setBusy(false) }
  }
  const projOpts = projects.map(p => ({ value: p.id, label: (p.zakazka_code ? p.zakazka_code + ' · ' : '') + (p.name || p.id) }))
  return (
    <Modal open title="Presunúť do dokumentov zákazky" onClose={onClose} width={520}
      footer={<><BtnGhost onClick={onClose}>Zrušiť</BtnGhost><BtnNav onClick={submit} disabled={busy}>{busy ? 'Presúvam…' : 'Presunúť'}</BtnNav></>}>
      <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.text, lineHeight: 1.5, marginBottom: SPACE.md }}>
        Doklad <b>{fa.supplier_name || fa.cislo || fa.realId}</b> sa presunie z Došlé FA do <b>Dokumentov zákazky</b> (príloha sa skopíruje). Z Došlých FA zmizne — <b>vratné cez „↩ Späť“</b>.
      </div>
      <div style={{ marginBottom: SPACE.sm }}>
        <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontWeight: TYPO.weights.semibold, display: 'block', marginBottom: 3 }}>Zákazka *</label>
        <SearchableSelect value={proj} onChange={setProj} options={projOpts} placeholder="— vyber zákazku —" />
      </div>
      <div>
        <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontWeight: TYPO.weights.semibold, display: 'block', marginBottom: 3 }}>Typ dokumentu</label>
        <input value={docType} onChange={e => setDocType(e.target.value)} style={{ width: '100%', padding: '7px 9px', fontSize: TYPO.sizes.sm, fontFamily: 'inherit', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, outline: 'none', boxSizing: 'border-box' }} />
      </div>
    </Modal>
  )
}

export function TrebaUhraditPane({ navigate }) {
  const toast = useToast()
  const [data, setData] = useState(null)
  const load = () => fetchAuth('/api/incoming-invoices/na-uhradu').then(r => r.ok ? r.json() : null).then(setData).catch(() => setData({ rows: [], konsolidacie: [], count: 0, suma: 0 }))
  useEffect(() => { load() }, [])
  useLiveRefresh(load)  // živé obnovenie (SSE, ústava 97)
  const markPaid = async (id) => {
    try { const r = await fetchAuth('/api/incoming-invoices/' + id + '/pay', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); if (!r.ok) throw new Error('HTTP ' + r.status); toast.success('Označené zaplatené'); load() } catch (e) { toast.error('Chyba: ' + e.message) }
  }
  const removeFromQueue = async (id) => {
    try { await fetchAuth('/api/incoming-invoices/' + id + '/na-uhradu', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ off: true }) }); toast.success('Stiahnuté z fronty'); load() } catch (e) { toast.error('Chyba: ' + e.message) }
  }
  if (!data) return <div style={{ padding: SPACE.xl, color: COLORS.muted }}>Načítavam…</div>
  const groups = data.konsolidacie || []
  const rowsById = {}; (data.rows || []).forEach(r => { rowsById[r.id] = r })
  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.md, padding: '10px 14px', background: 'rgba(14,165,233,0.10)', border: '1px solid #0ea5e9', borderRadius: RADIUS.md, flexWrap: 'wrap' }}>
        <span style={{ fontSize: TYPO.sizes.lg }}></span>
        <span style={{ fontWeight: 700, color: '#0369a1' }}>Treba uhradiť: {data.count || 0} faktúr · Σ {SK.eur(data.suma || 0)}</span>
        <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Rovnaký príjemca = 1 platba/QR/VS (transakčná daň). Po zaplatení označ „✓ Zaplatené".</span>
      </div>
      {groups.length === 0 ? <div style={{ padding: SPACE.xl, textAlign: 'center', color: COLORS.muted }}>Žiadne faktúry na úhradu. Zaraď ich v <b>Došlé FA → ⋮ → Treba uhradiť</b>.</div>
        : groups.map((g, i) => {
          const qr = buildGroupQr(g)
          return (
            <div key={i} style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, overflow: 'hidden', background: COLORS.bg }}>
              <div style={{ display: 'flex', gap: SPACE.lg, padding: SPACE.md, background: COLORS.bgSoft, borderBottom: `1px solid ${COLORS.line}`, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 700, fontSize: TYPO.sizes.md }}>{g.supplier || '—'}</div>
                  <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontFamily: TYPO.mono }}>IBAN: {g.iban || 'chýba'} · VS: {g.vs || '—'}</div>
                  <div style={{ marginTop: 4 }}>Σ <b style={{ fontFamily: TYPO.mono, color: COLORS.total }}>{SK.eur(g.suma)}</b> · {g.pocet} faktúr</div>
                </div>
                <GroupQr g={g} size={120} />
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {g.ids.map(id => { const fa = rowsById[id]; if (!fa) return null; return (
                    <tr key={id} style={{ borderBottom: `1px solid ${COLORS.lineSoft}` }}>
                      <td style={{ padding: '6px 10px', fontSize: TYPO.sizes.sm, cursor: 'pointer' }} onClick={() => navigate(`/faktury/${encodeURIComponent('I:' + id)}`)}>{fa.invoice_number || id}{fa.date_due ? ' · splatnosť ' + fa.date_due : (fa.date_issued ? ' · ' + fa.date_issued : '')}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: TYPO.mono, fontWeight: 600 }}>{SK.eur(fa.amount_total)}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button onClick={() => markPaid(id)} style={{ padding: '3px 10px', fontSize: TYPO.sizes.xs, fontWeight: 700, color: '#fff', background: COLORS.add, border: 'none', borderRadius: RADIUS.sm, cursor: 'pointer', marginRight: 6 }}>✓ Zaplatené</button>
                        <button onClick={() => removeFromQueue(id)} title="Stiahnuť z fronty" style={{ padding: '3px 8px', fontSize: TYPO.sizes.xs, color: COLORS.muted, background: 'transparent', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, cursor: 'pointer' }}>✕</button>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          )
        })}
    </div>
  )
}

export default function FakturyView() {
  const navigate = useNavigate()
  const { id: selectedId } = useParams()   // :id sa dekóduje automaticky (klik + šípky OK); view drží sessionStorage (skok do Vystavené vyriešený)
  const { user } = useAuth()
  const toast = useToast()
  const { setTrail } = useBreadcrumb()

  const [view, setView] = useState(() => { try { return sessionStorage.getItem('faktury_view') || 'vystavene' } catch (_) { return 'vystavene' } })
  useEffect(() => { try { sessionStorage.setItem('faktury_view', view) } catch (_) {} }, [view])
  // jedna_obrazovka_v1: doklad došlej FA (I:…) aj staré záložky idú do Došlých FA — jedna obrazovka, jedna pravda (0.J)
  useEffect(() => {
    if (selectedId && /^I:/.test(String(selectedId))) { navigate('/dosle-v3?id=' + encodeURIComponent(String(selectedId)), { replace: true }); return }
    if (PRESMEROVANIE[view]) { try { sessionStorage.setItem('faktury_view', 'vystavene') } catch (_) {}; setView('vystavene'); navigate('/dosle-v3?rozsah=' + PRESMEROVANIE[view], { replace: true }) }
  }, [view, selectedId])   // eslint-disable-line react-hooks/exhaustive-deps
  const [vystavene, setVystavene] = useState([])
  const [incoming, setIncoming] = useState([])
  const [pokladna, setPokladna] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [newModal, setNewModal] = useState(false)

  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState(() => { try { return sessionStorage.getItem('faktury_view') === 'vystavene' ? 'FVSP' : 'all' } catch (_) { return 'all' } })
  const [filterPay, setFilterPay] = useState('all')
  const [filterCategory, setFilterCategory] = useState('all')
  const [editItem, setEditItem] = useState(null)
  const [dateOd, setDateOd] = useState('')
  const [dateDo, setDateDo] = useState('')
  const [expenseModal, setExpenseModal] = useState(false)

  // Breadcrumb
  useEffect(() => {
    const lbl = SUBTABS.find(t => t.key === view)?.label || 'Faktúry'
    setTrail([
      { label: 'Matrix v2', onClick: () => navigate('/') },
      { label: 'Faktúry', onClick: () => navigate('/faktury') },
      { label: lbl }
    ])
    return () => setTrail([])
  }, [setTrail, navigate, view])

  // Reset filtra pri zmene podkarty
  // Vystavené FA = vždy default FVSP (nadradené, ústava sek. 7); ostatné pohľady = všetky typy
  useEffect(() => { setFilterType(view === 'vystavene' ? 'FVSP' : 'all'); setFilterPay('all'); setFilterCategory('all'); setSearch(''); setDateOd(''); setDateDo('') }, [view])

  const reload = useCallback(async (silent) => {
    if (!silent) setLoading(true)  // ústava 53.3 — tichý refresh po akcii nezhodí scroll/pozíciu
    try {
      const [mksoft, outgoing, incomingRaw, pokladnaRaw, projectsRaw] = await Promise.all([
        fetchAuth('/api/mksoft/invoices').then(r => r.ok ? r.json() : []).catch(() => []),
        fetchAuthJSON('/api/outgoing-invoices').catch(() => []),
        fetchAuthJSON('/api/incoming-invoices').catch(() => []),
        fetchAuthJSON('/api/pokladna').catch(() => []),
        fetchAuthJSON('/api/projects').catch(() => [])
      ])
      // faktury_raz_v1: MKsoft faktúry už prichádzajú v `outgoing` (UNION na serveri). Druhé načítanie
      // z /api/mksoft/invoices ich do zoznamu pridávalo ešte raz — hlavička hlásila 326, pod filtrom
      // bolo 119. Zoznam staviame z jedného zdroja; `mksoft` sa drží len pre prípadné doplnenie polí.
      const out = []
      ;(Array.isArray(outgoing) ? outgoing : []).forEach(o => out.push(normalizeOutgoing(o)))
      setVystavene(out.filter(f => f.typ !== 'FVO' && f.typ !== 'FVE'))  // FVO/FVE žijú v karte Predajňa & Eshop (ústava 45, 2026-07-06)
      setIncoming((Array.isArray(incomingRaw) ? incomingRaw : []).filter(i => i && !i._cudzi).map(normalizeIncoming))   // BLOCKY_BANKA_V3: cudzí bloček sa v zozname nezobrazuje
      // Pokladna — len reálne pohyby (prijem/vydaj), nie audit (create/edit/delete)
      setPokladna((Array.isArray(pokladnaRaw) ? pokladnaRaw : []).filter(p => p.typ === 'prijem' || p.typ === 'vydaj'))
      setProjects(Array.isArray(projectsRaw) ? projectsRaw : [])
    } catch (e) {
      toast.error(`Načítanie zlyhalo: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { reload() }, [reload])

  // Auto-scroll na vybranú položku — scrolluje IBA kontajner zoznamu (nie celú stránku!), s 1-riadkovým okrajom
  useEffect(() => {
    if (!selectedId) return
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-row-id="${selectedId}"]`)
      if (!el) return
      const cont = el.closest('[data-faktury-list]')
      if (!cont) { el.scrollIntoView({ block: 'nearest' }); return }
      const cr = cont.getBoundingClientRect(), er = el.getBoundingClientRect()
      const m = er.height   // okraj = výška jedného riadku, nech výber nie je nalepený na hrane
      if (er.top - m < cr.top) cont.scrollTop -= (cr.top - er.top + m)
      else if (er.bottom + m > cr.bottom) cont.scrollTop += (er.bottom + m - cr.bottom)
    }, 0)
    return () => clearTimeout(t)
  }, [selectedId])

  const isPokladna = view === 'pokladna'
  const isUctovanie = view === 'uctovanie'
  const isTreba = view === 'treba_uhradit'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.lg, height: 'calc(100vh - 130px)' }}>

      {/* HLAVICKA + PODKARTY */}
      {/* KOMPAKTNÝ MERGED TOPBAR — titul + taby (pill) + akcie v jednom riadku;
          zdieľa grid s KPI riadkom (FaPane) → taby zarovnané nad widgetmi (ústava 92.6) */}
      {(() => {
        const kpiCols = view === 'blocky' ? 2 : view === 'dosle' ? 5 : view !== 'vystavene' ? 4 : 3
        const counts = {
          vystavene: vystavene.length,
          dosle: incoming.filter(x => x.docType !== 'blocok' && !x.neo_import).length,
          blocky: incoming.filter(x => x.docType === 'blocok').length,
          import_neo: incoming.filter(x => x.neo_import).length,
          treba_uhradit: incoming.filter(x => x.na_uhradu && (x.status || '') !== 'paid' && !x.neo_import).length,
          pokladna: pokladna.length
        }
        return (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${kpiCols}, 1fr) 300px`, gap: SPACE.sm, alignItems: 'center', background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderTop: `3px solid ${COLORS.brand}`, borderRadius: 8, padding: '6px 14px' }}>
            <div style={{ gridColumn: 1, display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra }}>Faktúry</h1>
              {loading && <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs }}>…</span>}
            </div>
            <div style={{ gridColumn: '2 / -2', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderLeft: `1px solid ${COLORS.line}`, paddingLeft: 12, minWidth: 0 }}>
              {SUBTABS.filter(t => !t.roles || t.roles.includes(user?.role)).map(t => {
                const active = view === t.key
                return (
                  <button key={t.key} onClick={() => { try { sessionStorage.setItem('faktury_view', t.key) } catch (_) {}; setView(t.key); navigate('/faktury') }}
                    style={{ padding: '4px 13px', fontSize: TYPO.sizes.xs, fontWeight: active ? TYPO.weights.bold : TYPO.weights.semibold, whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit', borderRadius: 20, border: `1px solid ${active ? COLORS.nav : COLORS.line}`, background: active ? COLORS.nav : '#fff', color: active ? '#fff' : COLORS.muted }}>
                    {t.label}{t.noCount ? '' : ` (${counts[t.key]})`}
                  </button>
                )
              })}
            </div>
            <div style={{ gridColumn: '-2 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {(view === 'dosle' || view === 'blocky') && (<>
                <BtnNav size="sm" onClick={() => setExpenseModal(true)} title="Výdavok bez faktúry (drobný nákup bez dokladu)">+ Výdavok bez FA</BtnNav>
                <BtnAdd size="sm" onClick={() => setNewModal(true)}>+ Pridať {view === 'blocky' ? 'bloček' : 'došlú FA'}</BtnAdd>
              </>)}
            </div>
          </div>
        )
      })()}

      {/* Modal pridania doslej FA / blocku */}
      {newModal && (
        <NewIncomingModal
          open={newModal}
          docType={view === 'blocky' ? 'blocok' : 'invoice'}
          projects={projects}
          incoming={incoming}
          onClose={() => setNewModal(false)}
          onSaved={(created) => {
            setNewModal(false)
            reload(true)
            toast.success(`${view === 'blocky' ? 'Bloček' : 'Došlá FA'} ${created.invoice_number || created.id} uložená`)
            if (created && Number(created.kind_confirmed) === 0 && !created.invoice_kind) toast.info('Doklad uložený na rozdelenie — otvor ho a v Zaradení zvoľ Deliť.')   // p26
            if (created && created.duplicita) toast.error(`Pozor: uložené ako možný duplikát dokladu ${created.duplicita.existujuci} (${created.duplicita.dovod}). Skontroluj v Došlých FA a jeden z nich archivuj.`)
          }}
        />
      )}
      {expenseModal && (
        <VydavokBezFaModal
          open={expenseModal}
          projects={projects}
          onClose={() => setExpenseModal(false)}
          onSaved={(created) => {
            setExpenseModal(false)
            reload(true)
            toast.success(`Výdavok ${created.id || ''} uložený`)
          }}
        />
      )}
      {editItem && (
        <NewIncomingModal
          open={!!editItem}
          docType={editItem.docType === 'blocok' ? 'blocok' : 'invoice'}
          projects={projects}
          incoming={incoming}
          editItem={editItem}
          onClose={() => setEditItem(null)}
          onSaved={(saved) => {
            setEditItem(null)
            reload(true)
            toast.success(`Doklad ${saved.invoice_number || saved.id} upravený`)
          }}
        />
      )}

      {/* OBSAH podľa podkarty */}
      {isTreba ? (
        <TrebaUhraditPane navigate={navigate} />
      ) : isUctovanie ? (
        <UctovanieExportPane />
      ) : isPokladna ? (
        <PokladnaPane pokladna={pokladna} loading={loading}
          selectedId={selectedId} navigate={navigate} search={search} setSearch={setSearch} />
      ) : (
        <FaPane
          view={view}
          vystavene={vystavene}
          incoming={incoming}
          loading={loading}
          selectedId={selectedId}
          navigate={navigate}
          search={search} setSearch={setSearch}
          filterType={filterType} setFilterType={setFilterType}
          filterPay={filterPay} setFilterPay={setFilterPay}
          filterCategory={filterCategory} setFilterCategory={setFilterCategory}
          dateOd={dateOd} setDateOd={setDateOd}
          dateDo={dateDo} setDateDo={setDateDo}
          reload={reload}
          onEdit={(fa) => setEditItem(fa)}
        />
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// FA PANE — Vystavené / Došlé / Bločky (FA shape)
// ══════════════════════════════════════════════════════════════
function FaPane({ view, vystavene, incoming, loading, selectedId, navigate, search, setSearch, filterType, setFilterType, filterPay, setFilterPay, filterCategory, setFilterCategory, dateOd, setDateOd, dateDo, setDateDo, reload, onEdit }) {
  const confirm = useConfirm()
  const toast = useToast()
  const [sel, setSel] = useState(() => new Set())
  const [naUhraduModal, setNaUhraduModal] = useState(false)
  const [reclass, setReclass] = useState(null)
  const [bankMatch, setBankMatch] = useState(null) // BANKMATCH_2026-07-05
  const [localSel, setLocalSel] = useState(null)
  const effSel = localSel || selectedId
  useEffect(() => { setLocalSel(null) }, [selectedId])
  useEffect(() => { if (!effSel) return; try { const el = document.querySelector('[data-row-id="' + ((window.CSS && CSS.escape) ? CSS.escape(effSel) : effSel) + '"]'); if (el) el.scrollIntoView({ block: 'nearest' }) } catch (_) {} }, [effSel])
  const [filterKind, setFilterKind] = useState('all')
  const [projects, setProjects] = useState([])
  const [bulkProj, setBulkProj] = useState('')
  const [bulkCiel, setBulkCiel] = useState('')
  const [bulkKat, setBulkKat] = useState('')
  const [bulkBusy, setBulkBusy] = useState(false)
  const [leftCollapsed, setLeftCollapsed] = useState(false)
  const worklistMode = filterPay === 'needs_kind' || filterPay === 'na_urcenie'
  const toggleSel = (rid) => setSel(p => { const n = new Set(p); n.has(rid) ? n.delete(rid) : n.add(rid); return n })
  useEffect(() => {
    if (!worklistMode || projects.length) return
    fetchAuth('/api/projects?scope=all').then(r => r.ok ? r.json() : []).then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {})
  }, [worklistMode])
  const projMap = useMemo(() => {
    const m = {}
    ;(projects || []).forEach(p => { m[p.id] = (p.zakazka_code ? p.zakazka_code + ' · ' : '') + (p.name || p.id) })
    return m
  }, [projects])
  // Návrh „kam asi" z histórie dodávateľa (ústava 15.4) — len navrhne, nepriradí
  const suggestMap = useMemo(() => {
    const bySup = {}
    ;(incoming || []).forEach(d => {
      if (!d.kind_confirmed || !d.cost_center) return
      const sup = (d.supplier_name || '').trim().toLowerCase()
      if (!sup) return
      ;(bySup[sup] = bySup[sup] || []).push(d)
    })
    const mode = (arr, key, filt) => {
      const c = {}
      arr.forEach(x => { if (filt && !filt(x)) return; const v = x[key]; if (v) c[v] = (c[v] || 0) + 1 })
      let best = null, bestN = 0
      Object.keys(c).forEach(v => { if (c[v] > bestN) { best = v; bestN = c[v] } })
      return best
    }
    const out = {}
    Object.keys(bySup).forEach(sup => {
      const arr = bySup[sup]
      const cc = mode(arr, 'cost_center')
      out[sup] = { cost_center: cc, cost_category: mode(arr, 'cost_category', x => x.cost_center === cc && x.cost_category !== 'Čaká na určenie'), project_id: mode(arr, 'project_id', x => x.cost_center === 'zakazka') }
    })
    return out
  }, [incoming])
  const applySuggestion = async (fa, sug) => {
    const center = sug.cost_center
    const projLbl = sug.project_id ? (projMap[sug.project_id] || sug.project_id) : ''
    const lbl = (center === 'zakazka' ? ('na zákazku ' + projLbl) : 'na Správnu réžiu') + (sug.cost_category ? ' · ' + sug.cost_category : '')
    if (!sug.cost_category || sug.cost_category === 'Čaká na určenie') { toast.error('Návrh nemá kategóriu nákladu — otvor doklad a zaraď ho v Zaradení s kategóriou (ústava 14.0).'); return }
    const ok = await confirm({ title: 'Použiť návrh?', message: 'Doklad „' + (fa.supplier_name || fa.cislo || fa.realId) + '" sa zaradí ' + lbl + '. Pokračovať?', confirmLabel: 'Použiť' })
    if (!ok) return
    try {
      const r = await fetchAuth('/api/incoming-invoices/' + fa.realId + '/confirm-kind', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ invoice_kind: 'nakladova', cost_center: center, cost_category: sug.cost_category || '', project_id: center === 'zakazka' ? (sug.project_id || '') : '' }) })
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + r.status)) }
      toast.success('Zaradené ' + lbl)
      reload(true)
    } catch (e) { toast.error('Chyba: ' + e.message) }
  }
  // Hromadné zaradenie ako Zaradenie v detaile (ústava 14.0; Mirko 15. 9. 2026 „kedy vyberiem kategóriu???“): najprv typ,
  // nákladový vždy s kategóriou, na zákazku aj so zákazkou. Čo chýba a prečo, povie lišta (Kronika 484); neúplné neuloží ani server.
  const bulkChyba = []
  if (!bulkCiel) bulkChyba.push('typ zaradenia')
  if (bulkCiel === 'zakazka' && !bulkProj) bulkChyba.push('zákazka')
  if ((bulkCiel === 'rezia' || bulkCiel === 'zakazka') && !bulkKat) bulkChyba.push('kategória nákladu')
  const bulkAssign = async () => {
    const ids = [...sel]; if (!ids.length) return
    if (bulkChyba.length) { toast.error('Chýba: ' + bulkChyba.join(', ')); return }
    const proj = projects.find(p => p.id === bulkProj)
    const lbl = bulkCiel === 'evidencna' ? 'ako evidenčné (materiál na sklad, nie náklad)' : bulkCiel === 'zakazka' ? ('na zákazku ' + (proj ? (proj.zakazka_code || proj.name) : bulkProj) + ' · ' + bulkKat) : ('na Správnu réžiu · ' + bulkKat)
    const dosledok = bulkCiel === 'evidencna' ? 'Nebude sa rátať ako náklad firmy.' : bulkCiel === 'zakazka' ? 'Náklad sa prejaví vo výdavkoch zákazky.' : 'Náklad sa prejaví v Správnej réžii.'
    const ok = await confirm({ title: 'Hromadné zaradenie', message: ids.length + (ids.length === 1 ? ' doklad' : ids.length <= 4 ? ' doklady' : ' dokladov') + ' — zaradenie ' + lbl + '. ' + dosledok + ' Pokračovať?', confirmLabel: 'Zaradiť' })
    if (!ok) return
    const body = bulkCiel === 'evidencna'
      ? { invoice_kind: 'evidencna', cost_center: '', cost_category: '', project_id: '' }
      : { invoice_kind: 'nakladova', cost_center: bulkCiel, cost_category: bulkKat, project_id: bulkCiel === 'zakazka' ? bulkProj : '' }
    setBulkBusy(true)
    let hotovo = 0
    try {
      for (const rid of ids) {
        const r = await fetchAuth('/api/incoming-invoices/' + rid + '/confirm-kind', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
        if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + r.status)) }
        hotovo++
      }
      toast.success('Zaradených ' + hotovo + ' ' + lbl)
    } catch (e) { toast.error('Zaradených ' + hotovo + ' z ' + ids.length + '. Ďalší doklad sa nezaradil: ' + e.message) }
    finally { setBulkBusy(false); setSel(new Set(ids.slice(hotovo))); reload(true) }
  }
  const markNaUhradu = async (fa, off) => {
    try {
      const r = await fetchAuth('/api/incoming-invoices/' + fa.realId + '/na-uhradu', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ off: !!off }) })
      if (!r.ok) throw new Error('HTTP ' + r.status)
      toast.success(off ? 'Stiahnuté z „Treba uhradiť"' : 'Pridané do „Treba uhradiť" — majiteľ dostal upozornenie (NEO)')
      reload(true)
    } catch (e) { toast.error('Chyba: ' + e.message) }
  }
  const sourceItems = view === 'vystavene'
    ? vystavene
    : view === 'blocky'
      ? incoming.filter(x => x.docType === 'blocok')
      : incoming.filter(x => x.docType !== 'blocok')

  // Zoznam kategorii nakladov (z dat) — pre filter dropdown
  const categories = useMemo(() => {
    if (view === 'vystavene') return []
    const set = new Set()
    sourceItems.forEach(i => { if (i.cost_category) set.add(i.cost_category) })
    return [...set].sort()
  }, [sourceItems, view])

  // mesiace z dát — chip nastaví Obdobie (ústava sek. 66 triedenie/filter)
  const monthChips = useMemo(() => {
    const set = new Set(sourceItems.map(i => (i.date_issued || '').slice(0, 7)).filter(Boolean))
    return [...set].sort().reverse().map(ym => ({ key: ym, label: monthLabelFV(ym), od: ym + '-01', doo: ym + '-31' }))
  }, [sourceItems])

  const kpi = useMemo(() => {
    const now = new Date()
    const year = String(now.getFullYear())
    // Mirko 2026-05-26: widgety pre 'vystavene' = IBA FVSP (stavebné práce, ústava sek. 7).
    // FVO (obchod cash) + FVE (eshop) + ZA + DN majú iný cash-flow, neriešia sa tu.
    const baseItems = (view === 'vystavene'
      ? sourceItems.filter(i => deriveTyp(i) === 'FVSP')
      : sourceItems).filter(i => !i.neo_import) // Import NEO sa neráta do štatistík kým nie je zaradené
    // Hlavný widget — Mirko 2026-05-26:
    //   vystavené FVSP = CELKOVO (všetky obdobia)
    //   došlé FA = CELÝ ROK (aktuálny kalendárny rok)
    //   bločky = aktuálny mesiac (zachované)
    const primary = view === 'vystavene'
      ? baseItems
      : view === 'incoming'
        ? baseItems.filter(i => (i.date_issued || '').startsWith(year))
        : baseItems.filter(i => (i.date_issued || '').startsWith(now.toISOString().slice(0, 7)))
    const overdue = baseItems.filter(i => payStatus(i) === 'overdue')
    const unpaid = baseItems.filter(i => ['unpaid', 'partial', 'overdue'].includes(payStatus(i)))
    const noProject = view !== 'vystavene' ? baseItems.filter(i => !i.project_id) : []
    // Bez bankového sync (TODO sek. 17 / MEMORY) NEMÁME údaje o platbách.
    const hasPaymentData = baseItems.some(i => (i.amount_paid || 0) > 0 || i.status === 'paid' || i.status === 'uhradena')
    return {
      thisMonth: { count: primary.length, sum: primary.reduce((s, x) => s + x.amount_total, 0) },
      overdue: { count: overdue.length, sum: overdue.reduce((s, x) => s + (x.amount_total - x.amount_paid), 0) },
      unpaid: { count: unpaid.length, sum: unpaid.reduce((s, x) => s + (x.amount_total - x.amount_paid), 0) },
      noProject: { count: noProject.length, sum: noProject.reduce((s, x) => s + x.amount_total, 0) },
      hasPaymentData
    }
  }, [sourceItems, view])

// Derive typ z invoice_number prefix (Mirko 2026-05-26 — zjednotenie FVSP/FVSVP)
// invoices (MKsoft): cislo = '1xxxxxxx' / '2xxx' / '3xxx' / 'ZA…' / 'DN…' + typ='FVSVP'/'FVO'/'FVE'/'ZA'/'DN'
// outgoing_invoices (ručné): invoice_number = 'FVSP-…' / 'FVO-…' / 'ZA-…' / kde typ=NULL
function deriveTyp(fa) {
  if (fa.typ === 'FVSVP') return 'FVSP'   // MKsoft → UI label
  if (fa.typ) return fa.typ                // FVO/FVE/ZA/DN
  const n = String(fa.invoice_number || fa.cislo || '').toUpperCase()
  if (n.startsWith('FVSP') || n.startsWith('FVSVP') || /^1\d/.test(n)) return 'FVSP'
  if (n.startsWith('FVO')  || /^2\d/.test(n)) return 'FVO'
  if (n.startsWith('FVE')  || /^3\d/.test(n)) return 'FVE'
  if (n.startsWith('ZA'))   return 'ZA'
  if (n.startsWith('DN'))   return 'DN'
  return 'FVSP' // default pre ručné FA bez typu (stavebné práce)
}

  const filtered = useMemo(() => {
    return sourceItems.filter(fa => {
      // Import NEO = oddelená zložka pre lievik-FA, kým ju user nezaradí. Inde sa NEZOBRAZUJÚ.
      if (filterPay === 'import_neo') {
        if (!fa.neo_import) return false
      } else if (filterPay === 'na_urcenie') {
        if (!(fa.neo_import || needsAssign(fa))) return false
      } else {
        if (fa.neo_import) return false
        if (view === 'vystavene' && filterType !== 'all') {
          if (filterType === 'manual' && fa.from_mksoft) return false  // ručné = z outgoing_invoices (nie z MKsoft)
          if (filterType !== 'manual' && deriveTyp(fa) !== filterType) return false
        }
        if (filterPay === 'needs_kind') { if (!needsAssign(fa)) return false }
        else if (filterPay === 'na_uhradu') { if (!(fa.na_uhradu && (fa.status || '') !== 'paid')) return false }
        else if (filterPay !== 'all' && payStatus(fa) !== filterPay) return false
        if (filterCategory !== 'all' && (fa.cost_category || '') !== filterCategory) return false
        if (filterKind === 'evidencna' && fa.invoice_kind !== 'evidencna') return false
        if (filterKind === 'rezia' && fa.cost_center !== 'rezia') return false
        if (filterKind === 'zakazka' && fa.cost_center !== 'zakazka') return false
      }
      if (dateOd && (fa.date_issued || '') < dateOd) return false
      if (dateDo && (fa.date_issued || '') > dateDo) return false
      if (search.trim()) {
        const q = search.toLowerCase()
        return hladaj((fa.cislo || ''), q)
          || hladaj((fa.customer_name || ''), q)
          || hladaj((fa.supplier_name || ''), q)
          || hladaj((fa.note || ''), q)
      }
      return true
    }).sort((a, b) => (b.date_issued || '').localeCompare(a.date_issued || ''))
  }, [sourceItems, view, search, filterType, filterPay, filterCategory, filterKind, dateOd, dateDo])
  const faSort = useSortable(filtered)

  // KPI skladací (Mirko 2026-06-09 „widgety berú miesto") — pamätá si stav
  const [kpiHidden, setKpiHidden] = useState(() => { try { return sessionStorage.getItem('faktury_kpi_hidden') === '1' } catch (_) { return false } })
  const toggleKpi = () => setKpiHidden(h => { const n = !h; try { sessionStorage.setItem('faktury_kpi_hidden', n ? '1' : '0') } catch (_) {} return n })

  // Klavesova navigacia zoznamu — zdielany hook (ustava 54.2, 56.5)
  useListKeyboard({
    items: faSort.sorted,
    selectedId: effSel,
    onSelect: (id) => setLocalSel(id),
    onOpen: (row) => onEdit(row),
  })
  // Auto-focus zoznamu po načítaní/zmene tabu — aby šípky fungovali HNEĎ bez kliku do stránky
  useEffect(() => {
    const tag = document.activeElement && document.activeElement.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return  // nekradni fokus pri písaní
    const t = setTimeout(() => {
      const el = document.querySelector('[data-faktury-list]')
      if (el) { try { el.focus({ preventScroll: true }) } catch (_) { try { el.focus() } catch (_) {} } }
    }, 120)
    return () => clearTimeout(t)
  }, [view, loading])

  const selected = sourceItems.find(x => x.id === effSel)
  const isIncoming = view !== 'vystavene'
  const kpiLabel = view === 'vystavene' ? 'vystavené'
    : view === 'blocky' ? 'bločky' : 'došlé faktúry'

  return (
    <>
      {/* KPI — kompaktný grid (zdieľa stĺpce s topbarom → zarovnané) + skladací */}
      {kpiHidden ? (
        <div>
          <span onClick={toggleKpi} style={{ display: 'inline-block', fontSize: TYPO.sizes.xs, fontWeight: 700, color: COLORS.muted, cursor: 'pointer', border: `1px solid ${COLORS.line}`, borderRadius: 6, padding: '5px 9px', background: COLORS.bg }}>▸ zobraziť štatistiky</span>
        </div>
      ) : (
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${view === 'blocky' ? 2 : view === 'dosle' ? 5 : isIncoming ? 4 : 3}, 1fr) 300px`,
        gap: SPACE.sm, padding: '0 14px', border: '1px solid transparent', alignItems: 'stretch'
      }}>
        <KpiCard title={view === 'vystavene' ? 'Celkovo FVSP' : view === 'incoming' ? 'Tento rok došlé' : `Tento mesiac ${kpiLabel}`} count={kpi.thisMonth.count} sum={kpi.thisMonth.sum} color={COLORS.nav} />
        {view !== 'blocky' && (<>
        <KpiCard title={kpi.hasPaymentData ? 'Neuhradené' : 'Neuhradené'}
          count={kpi.unpaid.count} sum={kpi.unpaid.sum} color={COLORS.warn}
          onClick={() => setFilterPay('unpaid')} />
        <KpiCard title={kpi.hasPaymentData ? 'Po splatnosti' : 'Po splatnosti'}
          count={kpi.overdue.count} sum={kpi.overdue.sum} color={COLORS.remove}
          onClick={() => setFilterPay('overdue')} />
        </>)}
        {isIncoming && (
          <KpiCard title="Bez zákazky" count={kpi.noProject.count} sum={kpi.noProject.sum} color={COLORS.warn} />
        )}
        {view === 'dosle' && (() => {
          const nau = sourceItems.filter(x => x.na_uhradu && (x.status || '') !== 'paid' && !x.neo_import)
          const suma = nau.reduce((s, x) => s + (x.amount_total || 0), 0)
          return <KpiCard title="Treba uhradiť" count={nau.length} sum={suma} color="#0ea5e9" onClick={() => setFilterPay(filterPay === 'na_uhradu' ? 'all' : 'na_uhradu')} />
        })()}
        <span onClick={toggleKpi} title="Skryť štatistiky" style={{ gridColumn: '-2 / -1', justifySelf: 'end', alignSelf: 'center', fontSize: TYPO.sizes.xs, fontWeight: 700, color: COLORS.muted, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${COLORS.line}`, borderRadius: 6, padding: '5px 9px', background: COLORS.bg }}>▾ skryť štatistiky</span>
      </div>
      )}
      {/* Warning banner — ak chýbajú platby (Mirko 2026-05-26, ústava sek. 7 + 17) */}
      {view !== 'blocky' && !kpi.hasPaymentData && sourceItems.length > 0 && (
        <div style={{
          padding: '4px 12px', background: 'rgba(245, 158, 11, 0.12)',
          border: '1px solid rgba(245, 158, 11, 0.5)', borderRadius: RADIUS.md,
          fontSize: TYPO.sizes.xs, color: '#92400e', marginTop: SPACE.xs,
          lineHeight: 1.35,
        }} title="MKsoft sync neukladá platby (ústava sek. 7), bankový sync VÚB/George je TODO. Reálny stav úhrady zadáš ručne cez „Označiť uhradené“ na faktúre.">
          <strong>Stav platieb:</strong> úhrady sa párujú z výpisov VÚB (presná zhoda automaticky, ostatné Nastavenia → Banka). Ručne: menu FA → <strong>„Spárovať platbu z výpisu“</strong> alebo <strong>„Označiť uhradené“</strong>.
        </div>
      )}

      {/* Horizontálny filter pás — VŠETKY FA taby (ústava 90: filtre hore, ľavý stĺpec čistý) */}
      <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap', marginTop: SPACE.sm, padding: '8px 10px', background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md }}>
        {view === 'vystavene' && (<>
          <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontWeight: TYPO.weights.semibold }}>Typ:</span>
          {TYPES_OUT.map(t => (
            <button key={t.key} onClick={() => setFilterType(t.key)} title={t.hint || ''}
              style={{ padding: '3px 10px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: filterType === t.key ? '#fff' : t.color, background: filterType === t.key ? t.color : 'transparent', border: `1px solid ${t.color}`, borderRadius: RADIUS.full, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
            >{t.label}</button>
          ))}
        </>)}
        {view !== 'blocky' && (<>
          <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontWeight: TYPO.weights.semibold, marginLeft: view === 'vystavene' ? SPACE.sm : 0 }}>Stav:</span>
          {PAY_STATUSES.map(p => (
            <button key={p.key} onClick={() => setFilterPay(p.key)}
              style={{ padding: '3px 10px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: filterPay === p.key ? '#fff' : p.color, background: filterPay === p.key ? p.color : 'transparent', border: `1px solid ${p.color}`, borderRadius: RADIUS.full, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
            >{p.label}</button>
          ))}
        </>)}
        {categories.length > 0 && (
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            style={{ padding: '4px 8px', fontSize: TYPO.sizes.xs, fontFamily: 'inherit', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, background: COLORS.bg, outline: 'none', color: COLORS.text, marginLeft: SPACE.sm }}>
            <option value="all">Kategória — všetky</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontWeight: TYPO.weights.semibold, marginLeft: SPACE.sm }}>Obdobie:</span>
        <input type="date" value={dateOd} onChange={e => setDateOd(e.target.value)} title="Od dátumu"
          style={{ padding: '4px 6px', fontSize: TYPO.sizes.xs, fontFamily: 'inherit', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, outline: 'none' }} />
        <span style={{ color: COLORS.muted }}>–</span>
        <input type="date" value={dateDo} onChange={e => setDateDo(e.target.value)} title="Do dátumu"
          style={{ padding: '4px 6px', fontSize: TYPO.sizes.xs, fontFamily: 'inherit', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, outline: 'none' }} />
        {(dateOd || dateDo) && (
          <button onClick={() => { setDateOd(''); setDateDo('') }} title="Zrušiť filter dátumu"
            style={{ padding: '2px 6px', fontSize: TYPO.sizes.xs, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, background: COLORS.bg, cursor: 'pointer', color: COLORS.muted }}>✕</button>
        )}
        {/* Quick-actions presunuté z ľavého stĺpca (Mirko 2026-06-09) */}
        {isIncoming && (() => {
          const neoCount = sourceItems.filter(x => x.neo_import).length
          const needsCount = sourceItems.filter(x => !x.neo_import && needsAssign(x)).length
          const total = neoCount + needsCount
          if (!total && filterPay !== 'na_urcenie') return null
          const act = filterPay === 'na_urcenie'
          return <button onClick={() => setFilterPay(act ? 'all' : 'na_urcenie')} title="Doklady na určenie — z e-mailu (NEO) aj bez určeného typu. Zaškrtni doklad, vyber zákazku/typ a potvrď."
            style={{ marginLeft: SPACE.md, padding: '4px 12px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: act ? '#fff' : COLORS.warn, background: act ? COLORS.warn : COLORS.totalBg, border: `1px solid ${COLORS.warn}`, borderRadius: RADIUS.full, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Na určenie ({total}){neoCount ? ` · ${neoCount}× z NEO` : ''}</button>
        })()}
        {/* „Caká na určenie“ zlúené do „Na určenie“ (Mirko 2026-06-17) */}
        {view === 'dosle' && (() => {
          const nau = sourceItems.filter(x => x.na_uhradu && (x.status || '') !== 'paid' && !x.neo_import)
          if (!nau.length) return null
          const suma = nau.reduce((s, x) => s + (x.amount_total || 0), 0)
          const act = filterPay === 'na_uhradu'
          return (<>
            <button onClick={() => setFilterPay(act ? 'all' : 'na_uhradu')} title="Faktúry zaradené na zaplatenie"
              style={{ padding: '4px 12px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: act ? '#fff' : '#0369a1', background: act ? '#0ea5e9' : 'rgba(14,165,233,0.10)', border: '1px solid #0ea5e9', borderRadius: RADIUS.full, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Treba uhradiť ({nau.length}) · {SK.eur(suma)}</button>
            <button onClick={() => setNaUhraduModal(true)} title="Konsolidované QR platby"
              style={{ padding: '4px 12px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: '#fff', background: '#15803d', border: 'none', borderRadius: RADIUS.full, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Konsolidované QR</button>
            {naUhraduModal && <NaUhraduModal onClose={() => setNaUhraduModal(false)} />}
          </>)
        })()}
      </div>

      {/* MASTER-DETAIL */}
      <div style={{ display: 'flex', gap: SPACE.md, flex: 1, minHeight: 0 }}>
        {/* LAVY */}
        <div style={{
          width: leftCollapsed ? 0 : 300, flexShrink: 0, background: COLORS.bg,
          border: leftCollapsed ? 'none' : `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg,
          display: leftCollapsed ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden'
        }}>
          <div style={{ padding: SPACE.sm, borderBottom: `1px solid ${COLORS.line}` }}>
            <input
              data-faktury-search type="search"
              placeholder="Hľadať (číslo, partner, poznámka)…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '6px 10px', fontSize: TYPO.sizes.sm,
                fontFamily: TYPO.family, border: `1px solid ${COLORS.line}`,
                borderRadius: RADIUS.md, outline: 'none', boxSizing: 'border-box'
              }}
            />
          </div>
          {/* Filtre (Typ/Stav/Kategória/Obdobie) presunuté do horizontálneho pásu nad zoznam (ústava 90) */}
          {/* Čaká na určenie · Treba uhradiť · Konsolidované QR — presunuté HORE do filter pásu (Mirko 2026-06-09) */}
          {isIncoming && worklistMode && (
            <div style={{ padding: '0 ' + SPACE.sm + 'px ' + SPACE.sm + 'px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.text }}>Označené ({sel.size}) zaradiť ako:</div>
              <div role="group" aria-label="Typ zaradenia" style={{ display: 'flex', border: '1px solid ' + COLORS.line, borderRadius: RADIUS.sm, overflow: 'hidden' }}>
                {[['evidencna', 'Evidenčná'], ['rezia', 'Réžia'], ['zakazka', 'Zákazka']].map(([k, l]) => (
                  <button key={k} type="button" aria-pressed={bulkCiel === k} onClick={() => { setBulkCiel(k); setBulkKat('') }} style={{ flex: 1, padding: '5px 4px', border: 0, background: bulkCiel === k ? COLORS.nav : COLORS.bg, color: bulkCiel === k ? '#fff' : COLORS.text, fontFamily: 'inherit', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, cursor: 'pointer' }}>{l}</button>
                ))}
              </div>
              {bulkCiel === 'zakazka' && (
                <select value={bulkProj} onChange={e => setBulkProj(e.target.value)} aria-label="Zákazka (povinné)" style={{ width: '100%', padding: '5px 8px', fontSize: TYPO.sizes.xs, fontFamily: 'inherit', border: '1px solid ' + (sel.size && !bulkProj ? COLORS.remove : COLORS.line), borderRadius: RADIUS.sm, background: COLORS.bg, boxSizing: 'border-box' }}>
                  <option value="">Zákazka (povinné)…</option>
                  {projects.filter(p => !p.archived).map(p => <option key={p.id} value={p.id}>{(p.zakazka_code ? p.zakazka_code + ' · ' : '') + (p.name || p.id)}</option>)}
                </select>
              )}
              {(bulkCiel === 'rezia' || bulkCiel === 'zakazka') && (
                <select value={bulkKat} onChange={e => setBulkKat(e.target.value)} aria-label="Kategória nákladu (povinné)" style={{ width: '100%', padding: '5px 8px', fontSize: TYPO.sizes.xs, fontFamily: 'inherit', border: '1px solid ' + (sel.size && !bulkKat ? COLORS.remove : COLORS.line), borderRadius: RADIUS.sm, background: COLORS.bg, boxSizing: 'border-box' }}>
                  <option value="">Kategória nákladu (povinné)…</option>
                  {(bulkCiel === 'zakazka' ? ZAKAZKA_CATS_FD : REZIA_CATS_FD).map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              )}
              <button type="button" disabled={!sel.size || bulkBusy || bulkChyba.length > 0} onClick={bulkAssign} style={{ padding: '6px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: (!sel.size || bulkBusy || bulkChyba.length) ? COLORS.muted : '#fff', background: (!sel.size || bulkBusy || bulkChyba.length) ? COLORS.bgSoft : COLORS.add, border: '1px solid ' + ((!sel.size || bulkBusy || bulkChyba.length) ? COLORS.line : COLORS.add), borderRadius: RADIUS.sm, cursor: (!sel.size || bulkBusy || bulkChyba.length) ? 'default' : 'pointer', fontFamily: 'inherit' }}>{bulkBusy ? 'Zaraďujem…' : 'Zaradiť označené (' + sel.size + ')'}</button>
              {sel.size > 0 && <button type="button" onClick={() => setSel(new Set())} style={{ padding: 3, fontSize: TYPO.sizes.xs, color: COLORS.muted, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Zrušiť výber ({sel.size})</button>}
              <div role="status" style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: sel.size ? COLORS.remove : COLORS.muted }}>{!sel.size ? 'Najprv zaškrtni doklady v zozname.' : bulkChyba.length ? 'Chýba: ' + bulkChyba.join(', ') + '.' : ''}</div>
              <div style={{ fontSize: 10, color: COLORS.muted, lineHeight: 1.4 }}>{bulkCiel === 'evidencna' ? 'Evidenčná = materiál na sklad (VSZAK/VSOST v MKsofte), nie je náklad firmy; stredisko ani kategória sa nevypĺňajú.' : bulkCiel === 'zakazka' ? 'Povinné: zákazka a kategória. Bez zákazky náklad chýba vo výdavkoch zákazky, bez kategórie v prehľadoch a v predkontácii pre MKsoft.' : bulkCiel === 'rezia' ? 'Povinná: kategória. Bez nej sa Správna réžia nedá rozdeliť v prehľadoch ani predkontovať pre MKsoft.' : 'Vyber typ: Evidenčná (materiál na sklad), Réžia (náklad firmy bez zákazky) alebo Zákazka (priamy náklad zákazky). Potom doplň, čo je povinné (ústava 14.0).'}</div>
            </div>
          )}
          <div
            data-faktury-list
            tabIndex={0}
            style={{ flex: 1, overflow: 'auto', outline: 'none', padding: 6 }}
          >
            {loading ? (
              <div style={{ padding: SPACE.lg, color: COLORS.muted }}>Načítavam…</div>
            ) : faSort.sorted.length === 0 ? (
              <div style={{ padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
                {sourceItems.length === 0 ? 'Tu zatiaľ nič nie je.' : 'Žiadne podľa filtra.'}
              </div>
            ) : faSort.sorted.map(fa => {
              const isSel = fa.id === effSel
              const ps = payStatus(fa)
              const psCfg = PAY_STATUSES.find(p => p.key === ps) || PAY_STATUSES[0]
              const remain = fa.amount_total - fa.amount_paid
              const isNau = isIncoming && fa.na_uhradu && (fa.status || '') !== 'paid'  // Treba uhradiť — vizuálne potvrdenie
              return (
                <div key={fa.id} data-row-id={fa.id}
                  onClick={() => setLocalSel(fa.id)}
                  style={{
                    padding: '8px 10px', marginBottom: 6,
                    cursor: 'pointer', background: isNau ? 'rgba(14,165,233,0.14)' : COLORS.bg,
                    borderTop: `1px solid ${isSel ? COLORS.nav : COLORS.line}`,
                    borderRight: `1px solid ${isSel ? COLORS.nav : COLORS.line}`,
                    borderBottom: `1px solid ${isSel ? COLORS.nav : COLORS.line}`,
                    borderLeft: `4px solid ${view === 'blocky' ? COLORS.line : psCfg.color}`,
                    borderRadius: RADIUS.lg,
                    boxShadow: isSel ? `0 0 0 1px ${COLORS.nav}` : '0 1px 2px rgba(0,0,0,0.05)',
                    fontSize: TYPO.sizes.sm, userSelect: 'none'
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)' }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                    {isIncoming && worklistMode && (
                      <input type="checkbox" checked={sel.has(fa.realId)} onClick={e => e.stopPropagation()} onChange={() => toggleSel(fa.realId)} style={{ width: 16, height: 16, marginTop: 2, accentColor: '#0ea5e9', cursor: 'pointer', flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* R1: NÁZOV + stav badge (Zákazky štýl, ústava 92.4) */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                        <div style={{ flex: 1, minWidth: 0, fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.base, color: isSel ? COLORS.nav : COLORS.text, lineHeight: 1.25, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {isIncoming ? (fa.supplier_name || '— bez dodávateľa —') : (fa.customer_name || '— bez zákazníka —')}
                        </div>
                        {view !== 'blocky' && <span style={{ flexShrink: 0 }}>{badge(psCfg.label, psCfg.color, psCfg.icon)}</span>}
                        {fa.neo_import ? <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, color: '#0369a1', background: 'rgba(3,105,161,0.12)', border: '1px solid #0369a1', borderRadius: 4, padding: '0 4px', alignSelf: 'center' }}>NEO</span> : null}
                      </div>
                      {/* R2: číslo · dátum (malé sivé) */}
                      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontFamily: TYPO.mono, margin: '3px 0 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {fa.cislo || '(bez čísla)'} · {fa.date_issued || '—'}{fa.date_due ? ` → ${fa.date_due}` : ''}
                      </div>
                      {/* R3: bez zákazky / návrh vľavo · suma vpravo */}
                      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {isIncoming && !fa.project_id && (
                            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.warn, fontWeight: TYPO.weights.semibold }}>bez zákazky</div>
                          )}
                          {isIncoming && worklistMode && (() => {
                            const sug = suggestMap[(fa.supplier_name || '').trim().toLowerCase()]
                            if (!sug || !sug.cost_center) return (
                              <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 1, fontStyle: 'italic' }}>bez histórie — zaraď ručne</div>
                            )
                            const cName = sug.cost_center === 'zakazka' ? 'Na zákazku' : sug.cost_center === 'rezia' ? 'Réžia' : sug.cost_center
                            const projLbl = sug.project_id ? (projMap[sug.project_id] || sug.project_id) : ''
                            const complete = !!sug.cost_category && sug.cost_category !== 'Čaká na určenie' && (sug.cost_center === 'rezia' || (sug.cost_center === 'zakazka' && !!sug.project_id))
                            return (
                              <div onClick={e => e.stopPropagation()} style={{ marginTop: 1, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 10, color: '#0369a1', background: '#e0f2fe', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>návrh: {cName}{projLbl ? ' · ' + projLbl : ''}{sug.cost_category ? ' · ' + sug.cost_category : ' · bez kategórie'}</span>
                                {complete && (
                                  <button onClick={() => applySuggestion(fa, sug)} style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#0ea5e9', border: 'none', borderRadius: 4, padding: '2px 7px', cursor: 'pointer', fontFamily: 'inherit' }}>✓ použiť</button>
                                )}
                              </div>
                            )
                          })()}
                        </div>
                        <div style={{ flexShrink: 0, fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.bold, fontFamily: TYPO.mono, whiteSpace: 'nowrap' }}>{SK.eur(fa.amount_total)}</div>
                      </div>
                    </div>
                    <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0, alignSelf: 'flex-start', marginLeft: 4 }}>
                      <RowMenu compact actions={isIncoming ? [
                        { key: 'open',  label: 'Otvoriť detail',    icon: '', onClick: () => navigate(`/faktury/${encodeURIComponent(fa.id)}`) },
                        ...(view === 'dosle' ? [{ key: 'nau', label: fa.na_uhradu ? 'Stiahnuť z „Treba uhradiť"' : 'Treba uhradiť', icon: '', onClick: () => markNaUhradu(fa, !!fa.na_uhradu) }] : []),
                        { key: 'pdf',   label: 'Uložiť ako PDF',    icon: '', onClick: () => alert('PDF — pripravujeme') },
                        { key: 'att',   label: 'Stiahnuť prílohu',  icon: '', onClick: () => {
                          const _att = fa.attachment_url || (fa.attachment_path ? '/uploads/' + fa.attachment_path : (fa.doc_file ? '/uploads/' + fa.doc_file : ''))
                          if (_att) {
                            window.open(_att, '_blank')
                          } else {
                            toast.info('Táto faktúra nemá nahranú prílohu')
                          }
                        } },
                        ...(fa.doc_file ? [{ key: 'tosupis', label: 'Presunúť do dokumentov zákazky', onClick: () => setReclass(fa) }] : []),
                        { key: 'pay',   label: 'Zaplatiť',          icon: '', onClick: () => alert('Zaplatiť — pripravujeme') },
                        { separator: true },
                        { key: 'del',   label: 'Vymazať', icon: '', danger: true, onClick: () => alert('Vymazanie — potvrď v karte') },
                      ] : [
                        { key: 'open',  label: 'Otvoriť detail',    icon: '', onClick: () => navigate(`/faktury/${encodeURIComponent(fa.id)}`) },
                        { key: 'print', label: 'Tlačiť',            icon: '', onClick: () => alert('Tlač — pripravujeme') },
                        { key: 'pdf',   label: 'Uložiť ako PDF',    icon: '', onClick: () => alert('PDF — pripravujeme') },
                        { key: 'copy',  label: 'Vytvoriť kópiu',    icon: '', onClick: () => alert('Kópia — pripravujeme') },
                        { key: 'email', label: 'Odoslať e-mailom',  icon: '', onClick: () => alert('E-mail — pripravujeme') },
                        { key: 'bankmatch', label: 'Spárovať platbu z výpisu', icon: '', onClick: () => setBankMatch(fa) },
                        { key: 'paid',  label: 'Označiť ako uhradenú', icon: '✓', onClick: async () => {
                          const ok = await confirm({
                            title: 'Označiť faktúru ako uhradenú?',
                            message: `Faktúra ${fa.cislo || fa.id} (${fa.customer_name || ''}) sa označí ako plne uhradená.`,
                            confirmLabel: 'Označiť',
                          })
                          if (!ok) return
                          try {
                            // cisla_v1: fa.id je prefixované zdrojom ('M:'/'O:'), server pozná len skutočné id.
                            if (fa.source === 'mksoft') { toast.info('Faktúra je z MKsoftu — úhradu zaeviduj tam, Matrix si ju natiahne.'); return }
                            const r = await fetchAuth(`/api/outgoing-invoices/${fa.realId || fa.id}/mark-paid`, { method: 'PATCH' })
                            if (!r.ok) { const e2 = await r.json().catch(() => ({})); throw new Error(e2.error || 'nepodarilo sa označiť') }
                            toast.success('Označené ako uhradené')
                            reload(true)
                          } catch (e) { toast.error('Chyba: ' + e.message) }
                        } },
                        { key: 'lock',  label: 'Zamknúť doklad',    icon: '', onClick: async () => {
                          const ok = await confirm({
                            title: 'Zamknúť doklad?',
                            message: `Faktúra ${fa.cislo || fa.id} sa zamkne — žiadne ďalšie úpravy. Odomknúť môže len admin.`,
                            confirmLabel: 'Zamknúť',
                          })
                          if (!ok) return
                          try {
                            // fa_banka_v1: fa.id je 'M:INV202' (predpona zdroja z normalizeOutgoing),
                            // server hľadá skutočné 'INV202' — bez odstránenia predpony vždy 404.
                            const _realId = String(fa.id || '').replace(/^[MO]:/, '')
                            const r = await fetchAuth(`/api/outgoing-invoices/${encodeURIComponent(_realId)}/lock`, { method: 'PATCH' })
                            if (!r.ok) throw new Error('PATCH zlyhal')
                            toast.success('Doklad zamknutý')
                            reload(true)
                          } catch (e) { toast.error('Chyba: ' + e.message) }
                        } },
                        { separator: true },
                        { key: 'del',   label: 'Vymazať', icon: '', danger: true, onClick: () => alert('Vymazanie — potvrď v karte') },
                      ]} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          <div style={{ padding: '6px 12px', borderTop: `1px solid ${COLORS.line}`, fontSize: TYPO.sizes.xs, color: COLORS.muted, background: COLORS.bgSoft }}>
            ↑↓ select · / hľadať · {filtered.length} zobrazených
          </div>
        </div>

        <button onClick={() => setLeftCollapsed(c => !c)} title={leftCollapsed ? 'Zobraziť zoznam faktúr' : 'Skryť zoznam — širší detail'} style={{ width: 22, flexShrink: 0, alignSelf: 'stretch', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, background: COLORS.bgSoft, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, color: COLORS.muted, padding: 0 }}>{leftCollapsed ? '▶' : '◀'}</button>
        {/* PRAVY */}
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {selected ? (
            <FaDetailPane fa={selected} onReload={reload} onEdit={onEdit} menuActions={selected ? (selected.source === 'incoming' ? [
              { key: 'nau', label: selected.na_uhradu ? 'Stiahnuť z „Treba uhradiť"' : 'Treba uhradiť', onClick: () => markNaUhradu(selected, !!selected.na_uhradu) },
              { key: 'att', label: 'Stiahnuť prílohu', onClick: () => { const a = selected.attachment_url || (selected.doc_file ? '/uploads/' + selected.doc_file : ''); if (a) window.open(a, '_blank'); else toast.info('Bez prílohy') } },
              ...(selected.doc_file ? [{ key: 'tosupis', label: 'Presunúť do dokumentov zákazky', onClick: () => setReclass(selected) }] : []),
              { key: 'payinc', label: 'Označiť ako zaplatenú', onClick: async () => {
                const ok = await confirm({ title: 'Označiť došlú FA ako zaplatenú?', message: `Doklad ${selected.cislo || selected.supplier_name || selected.id} sa označí ako uhradený (dnešným dátumom).`, confirmLabel: 'Označiť' })
                if (!ok) return
                try {
                  const r = await fetchAuth(`/api/incoming-invoices/${encodeURIComponent(selected.realId || selected.id)}/pay`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ payment_note: 'ručne z detailu' }) })
                  if (!r.ok) throw new Error('POST zlyhal')
                  toast.success('Označené ako zaplatené'); reload(true)
                } catch (e) { toast.error('Chyba: ' + e.message) }
              } },
            ] : [
              { key: 'bankmatch', label: 'Spárovať platbu z výpisu', onClick: () => setBankMatch(selected) },
              ...(selected.source === 'matrix' ? [{ key: 'paidout', label: 'Označiť ako uhradenú', onClick: async () => {
                const ok = await confirm({ title: 'Označiť faktúru ako uhradenú?', message: `Faktúra ${selected.cislo || selected.realId} (${selected.customer_name || ''}) sa označí ako plne uhradená.`, confirmLabel: 'Označiť' })
                if (!ok) return
                try {
                  const r = await fetchAuth(`/api/outgoing-invoices/${encodeURIComponent(selected.realId || selected.id)}/mark-paid`, { method: 'PATCH' })
                  if (!r.ok) throw new Error('PATCH zlyhal')
                  toast.success('Označené ako uhradené'); reload(true)
                } catch (e) { toast.error('Chyba: ' + e.message) }
              } }] : []),
              { key: 'copyfa', label: 'Kopírovať údaje FA', onClick: () => { try { navigator.clipboard.writeText(`${selected.cislo} · ${selected.customer_name || ''} · ${selected.date_issued} · ${selected.amount_total} €`) } catch (_) {} } },
            ]) : []} />
          ) : (
            <EmptyPane text={filtered.length > 0 ? 'Vyber faktúru zo zoznamu' : 'Žiadne faktúry v aktuálnom filtri'} />
          )}
        </div>
      </div>
      {reclass && <ReclassModal fa={reclass} onClose={() => setReclass(null)} onDone={reload} />}
      {bankMatch && <BankMatchModal fa={bankMatch} onClose={() => setBankMatch(null)} onDone={() => reload(true)} />}
    </>
  )
}

// ══════════════════════════════════════════════════════════════
// POKLADŇA PANE — hotovostné pohyby
// ══════════════════════════════════════════════════════════════
function PokladnaPane({ pokladna, loading, selectedId, navigate, search, setSearch }) {
  const [filterTyp, setFilterTyp] = useState('all')

  // KPI
  const kpi = useMemo(() => {
    const now = new Date()
    const month = now.toISOString().slice(0, 7)
    const monthRows = pokladna.filter(p => (p.datum || '').startsWith(month))
    const prijemMes = monthRows.filter(p => p.typ === 'prijem').reduce((s, p) => s + (p.suma || 0), 0)
    const vydajMes = monthRows.filter(p => p.typ === 'vydaj').reduce((s, p) => s + (p.suma || 0), 0)
    // posledny zostatok = posledny zaznam (uz zoradene ASC z API)
    const last = pokladna.length ? pokladna[pokladna.length - 1] : null
    return {
      zostatok: last ? (last.zostatok || 0) : 0,
      prijemMes, vydajMes
    }
  }, [pokladna])

  const filtered = useMemo(() => {
    return pokladna.filter(p => {
      if (filterTyp !== 'all' && p.typ !== filterTyp) return false
      if (search.trim()) {
        const q = search.toLowerCase()
        return hladaj((p.popis || ''), q)
          || hladaj((p.kategoria || ''), q)
      }
      return true
    }).sort((a, b) => (b.datum || '').localeCompare(a.datum || ''))
  }, [pokladna, search, filterTyp])

  const selected = pokladna.find(p => ('P:' + p.id) === selectedId)
  // Klavesova navigacia zoznamu (ustava 54.2)
  const kbItems = useMemo(() => filtered.map(p => ({ id: 'P:' + p.id })), [filtered])
  useListKeyboard({
    items: kbItems,
    selectedId,
    onSelect: (id) => navigate(`/faktury/${encodeURIComponent(id)}`),
  })

  return (
    <>
      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: SPACE.md }}>
        <KpiCard title="Aktuálny zostatok" value={SK.eur(kpi.zostatok)} color={kpi.zostatok >= 0 ? COLORS.add : COLORS.remove} highlight />
        <KpiCard title="Príjmy tento mesiac" value={SK.eur(kpi.prijemMes)} color={COLORS.add} />
        <KpiCard title="Výdavky tento mesiac" value={SK.eur(kpi.vydajMes)} color={COLORS.remove} />
      </div>

      {/* MASTER-DETAIL */}
      <div style={{ display: 'flex', gap: SPACE.md, flex: 1, minHeight: 0 }}>
        <div style={{
          width: 300, flexShrink: 0, background: COLORS.bg,
          border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg,
          display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}>
          <div style={{ padding: SPACE.sm, borderBottom: `1px solid ${COLORS.line}` }}>
            <input type="search" placeholder="Hľadať (popis, kategória)…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '6px 10px', fontSize: TYPO.sizes.sm,
                fontFamily: TYPO.family, border: `1px solid ${COLORS.line}`,
                borderRadius: RADIUS.md, outline: 'none', boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ padding: SPACE.sm, display: 'flex', gap: 2, borderBottom: `1px solid ${COLORS.line}` }}>
            {[
              { key: 'all', label: 'Všetky', color: COLORS.muted },
              { key: 'prijem', label: 'Príjem', color: COLORS.add },
              { key: 'vydaj', label: 'Výdaj', color: COLORS.remove }
            ].map(t => (
              <button key={t.key} onClick={() => setFilterTyp(t.key)}
                style={{
                  flex: 1, padding: '3px 6px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold,
                  color: filterTyp === t.key ? '#fff' : t.color,
                  background: filterTyp === t.key ? t.color : 'transparent',
                  border: `1px solid ${t.color}`, borderRadius: RADIUS.sm, cursor: 'pointer', fontFamily: 'inherit'
                }}
              >{t.label}</button>
            ))}
          </div>
          <div data-faktury-list tabIndex={0} style={{ flex: 1, overflow: 'auto', outline: 'none', padding: 6 }}>
            {loading ? (
              <div style={{ padding: SPACE.lg, color: COLORS.muted }}>Načítavam…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Žiadne pohyby.</div>
            ) : filtered.map(p => {
              const rid = 'P:' + p.id
              const isSel = rid === selectedId
              const isPrijem = p.typ === 'prijem'
              return (
                <div key={p.id} data-row-id={rid}
                  onClick={() => navigate(`/faktury/${encodeURIComponent(rid)}`)}
                  style={{
                    padding: '8px 10px', marginBottom: 6,
                    cursor: 'pointer', background: COLORS.bg,
                    borderTop: `1px solid ${isSel ? COLORS.nav : COLORS.line}`,
                    borderRight: `1px solid ${isSel ? COLORS.nav : COLORS.line}`,
                    borderBottom: `1px solid ${isSel ? COLORS.nav : COLORS.line}`,
                    borderLeft: `4px solid ${isPrijem ? COLORS.add : COLORS.remove}`,
                    borderRadius: RADIUS.lg,
                    boxShadow: isSel ? `0 0 0 1px ${COLORS.nav}` : '0 1px 2px rgba(0,0,0,0.05)',
                    fontSize: TYPO.sizes.sm, userSelect: 'none'
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)' }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, color: COLORS.text, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                        {p.popis || '(bez popisu)'}
                      </div>
                      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 2 }}>
                        {p.datum || '—'} {p.kategoria ? `· ${p.kategoria}` : ''}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{
                        fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold, fontFamily: TYPO.mono,
                        color: isPrijem ? COLORS.add : COLORS.remove
                      }}>{isPrijem ? '+' : '−'}{SK.eur(p.suma || 0)}</div>
                      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontFamily: TYPO.mono, marginTop: 2 }}>
                        zostatok {SK.eur(p.zostatok || 0)}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          <div style={{ padding: '6px 12px', borderTop: `1px solid ${COLORS.line}`, fontSize: TYPO.sizes.xs, color: COLORS.muted, background: COLORS.bgSoft }}>
            {filtered.length} pohybov · hotovostná pokladňa
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {selected ? (
            <PokladnaDetail p={selected} />
          ) : (
            <EmptyPane text={filtered.length > 0 ? 'Vyber pohyb zo zoznamu' : 'Žiadne pohyby'} />
          )}
        </div>
      </div>
    </>
  )
}

function PokladnaDetail({ p }) {
  const isPrijem = p.typ === 'prijem'
  const rows = [
    ['Dátum',       p.datum || '—'],
    ['Typ',         isPrijem ? 'Príjem' : 'Výdaj'],
    ['Suma',        (isPrijem ? '+' : '−') + SK.eur(p.suma || 0)],
    ['Zostatok po', SK.eur(p.zostatok || 0)],
    ['Kategória',   p.kategoria || '—'],
    ['Zákazka',     p.project_id || '— bez zákazky —'],
    ['Zamestnanec', p.employee_id || '—'],
    ['Poznámka',    p.poznamka || '—']
  ]
  return (
    <div style={{
      background: COLORS.bg, border: `1px solid ${COLORS.line}`,
      borderRadius: RADIUS.lg, padding: SPACE.lg, height: '100%', overflow: 'auto'
    }}>
      <div style={{ height: 4, background: COLORS.brand, margin: `-${SPACE.lg}px -${SPACE.lg}px ${SPACE.md}px` }} />
      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE.md, marginBottom: SPACE.md }}>
        <h2 style={{ margin: 0, fontSize: TYPO.sizes.xl, fontWeight: TYPO.weights.extra }}>{p.popis || '(bez popisu)'}</h2>
        <span style={{
          padding: '2px 8px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold,
          color: isPrijem ? COLORS.add : COLORS.remove,
          background: isPrijem ? COLORS.addBg : COLORS.removeBg,
          border: `1px solid ${isPrijem ? COLORS.add : COLORS.remove}`,
          borderRadius: RADIUS.full
        }}>{isPrijem ? 'Príjem' : 'Výdaj'}</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} style={{ borderBottom: `1px solid ${COLORS.lineSoft}` }}>
              <td style={{ padding: '6px 8px', color: COLORS.muted, width: 180 }}>{k}</td>
              <td style={{ padding: '6px 8px', fontWeight: TYPO.weights.semibold }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: SPACE.md, padding: SPACE.sm, background: COLORS.bgSoft, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
        Pokladňa je hotovostná evidencia firmy. Pridávanie pohybov pribudne neskôr.
      </div>
    </div>
  )
}



// ══════════════════════════════════════════════════════════════
// NEW INCOMING MODAL — pridanie došlej FA / bločku
// Krok 1: nahrať súbor (PDF/foto) → AI extrakcia (Claude Vision) ALEBO vyplniť ručne
// Krok 2: formulár (predvyplnený AI)
// Krok 3: potvrdenie → POST /api/incoming-invoices
// ══════════════════════════════════════════════════════════════
const COST_CATS = ['materiál', 'doprava', 'réžia', 'prenájom', 'servis', 'mzdy', 'iné']

// ── Cost accounting — port zo starého IncomingInvoicesView.jsx (sek. 14, 12.4) ──
const KIND_CONFIG = {
  evidencna: { label: 'Evidenčná', hint: 'Faktúra ide na sklad (VSZAK/VSOST riešime v MKsoft). Eviduje sa iba pre prehľad celkových výdavkov — NIE je to priamy náklad firmy.' },
  nakladova: { label: 'Nákladová', hint: 'Skutočný výdavok firmy — vyber stredisko a kategóriu.' }
}
const CENTER_CONFIG = {
  rezia:   { label: 'Správna réžia', icon: '', color: '#8b5cf6' },
  zakazka: { label: 'Na zákazku',    icon: '', color: COLORS.total }
}
const REZIA_CATS = [
  'Mzdy a odmeny', 'Odvody / Sociálna poisťovňa', 'PHM (pohonné hmoty)', 'Poistenie',
  'Prenájom priestorov', 'Leasing vozidla', 'Opravy áut / servis', 'Splátka úveru',
  'Bankové poplatky', 'Telefón / Internet', 'Účtovníctvo / Právne služby', 'IT a technika',
  'Kancelárske potreby', 'Reštaurácie / Stravovanie', 'Nákup náradia / zariadenia',
  'Oprava náradia / servis', 'Prenájom náradia', 'Notár / overenie / poplatky', 'Iné réžijné náklady'
]
const ZAKAZKA_CATS = [
  'Stavebný materiál', 'Zriadenie staveniska', 'Mzdy a odmeny', 'PHM (pohonné hmoty — zákazka)',
  'Nákup náradia / zariadenia', 'Oprava náradia / servis', 'Prenájom náradia',
  'Subdodávka práce', 'Doprava na zákazku', 'Prenájom strojov', 'Iný zákazkový výdavok'
]

// KindSelector — 2 veľké tlačidlá Evidenčná / Nákladová (1 klik = rýchlosť)
function KindSelector({ value, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.sm }}>
      {Object.entries(KIND_CONFIG).map(([key, cfg]) => {
        const active = value === key
        return (
          <button key={key} type="button" onClick={() => onChange(key)}
            style={{
              padding: '12px 14px', textAlign: 'left', cursor: 'pointer',
              border: `2px solid ${active ? COLORS.nav : COLORS.line}`,
              borderRadius: RADIUS.md,
              background: active ? COLORS.navBg : COLORS.bg,
              fontFamily: 'inherit'
            }}>
            <div style={{ fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.md, color: active ? COLORS.nav : COLORS.text, marginBottom: 3 }}>{cfg.label}</div>
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, lineHeight: 1.4 }}>{cfg.hint}</div>
          </button>
        )
      })}
    </div>
  )
}

// CenterSelector — Réžia / Zákazka (1 klik, len ak Nákladová)
function CenterSelector({ value, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.sm }}>
      {Object.entries(CENTER_CONFIG).map(([key, cfg]) => {
        const active = value === key
        return (
          <button key={key} type="button" onClick={() => onChange(key)}
            style={{
              padding: '10px 14px', textAlign: 'center', cursor: 'pointer',
              border: `2px solid ${active ? cfg.color : COLORS.line}`,
              borderRadius: RADIUS.md,
              background: active ? `${cfg.color}18` : COLORS.bg,
              color: active ? cfg.color : COLORS.text,
              fontWeight: TYPO.weights.semibold, fontSize: TYPO.sizes.base,
              fontFamily: 'inherit'
            }}>
            {cfg.icon} {cfg.label}
          </button>
        )
      })}
    </div>
  )
}

// CategoryChips — kategória nákladu jedným klikom (rýchlosť, sek. 54)
function CategoryChips({ cats, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {cats.map(c => {
        const active = value === c
        return (
          <button key={c} type="button" onClick={() => onChange(c)}
            style={{
              padding: '3px 10px', fontSize: TYPO.sizes.xs, cursor: 'pointer',
              border: `1px solid ${active ? COLORS.nav : COLORS.line}`,
              borderRadius: RADIUS.full,
              background: active ? COLORS.nav : COLORS.bg,
              color: active ? '#fff' : COLORS.text,
              fontWeight: active ? TYPO.weights.semibold : TYPO.weights.normal,
              fontFamily: 'inherit'
            }}>
            {c}
          </button>
        )
      })}
    </div>
  )
}

// learnFromSupplier — Matrix sa učí typ výdavku z histórie dodávateľa (ústava 15.4 / 12.10)
// Vráti najčastejšie zaradenie (invoice_kind + cost_center + cost_category) z predošlých FA.
function learnFromSupplier(ico, name, list) {
  if (!Array.isArray(list) || !list.length) return null
  const icoTrim = (ico || '').trim()
  const nameTrim = (name || '').trim().toLowerCase()
  const prev = list.filter(fa => {
    if (icoTrim && fa.supplier_ico) return String(fa.supplier_ico).trim() === icoTrim
    if (nameTrim) return (fa.supplier_name || '').trim().toLowerCase() === nameTrim
    return false
  }).filter(fa => fa.invoice_kind)   // len tie čo majú vyplnený typ
  if (!prev.length) return null
  const count = {}
  prev.forEach(fa => {
    const key = `${fa.invoice_kind || ''}|${fa.cost_center || ''}|${fa.cost_category || ''}`
    count[key] = (count[key] || 0) + 1
  })
  const best = Object.entries(count).sort((a, b) => b[1] - a[1])[0]
  if (!best) return null
  const [invoice_kind, cost_center, cost_category] = best[0].split('|')
  return { invoice_kind, cost_center, cost_category, total: prev.length, hits: best[1] }
}

export function NewIncomingModal({ open, docType, projects, incoming = [], editItem, onClose, onSaved, initialFile }) {
  const toast = useToast()
  const pinGate = usePinGate()
  const { user } = useAuth()
  // P1.2_USELOCK_2026-05-30: zámok došlej faktúry/bločka pri editácii (ústava sek. 53.7)
  const { lockInfo, acquired } = useLock(
    (open && editItem?.id) ? 'incoming_invoice' : null,
    (open && editItem?.id) ? editItem.id : null,
    user
  )
  const isLocked = !!editItem?.id && !acquired && !!lockInfo?.by
  // PIN handler — exaktná duplicita dokladu override (ústava 13.2)
  // Použitie: ak backend vráti 409 s error: 'duplicate_exact' (alebo podobne), zavolaj:
  //   const ok = await handleDupOverride({ ico, cislo, suma, existujuci_id })
  //   if (ok) retry s body { force_dup_override: true, pin_audit_id: ok.audit_id }
  const handleDupOverride = async (ctx) => {
    const gate = await pinGate('duplicita_dokladu', {
      ico: ctx.ico || '—',
      cislo_dokladu: ctx.cislo || '—',
      suma_EUR: ctx.suma || 0,
      existujuci_id: ctx.existujuci_id || '—',
    })
    return (gate && gate.ok) ? gate : null
  }
  const isBlocok = docType === 'blocok'
  const isEdit = !!editItem
  // BLOCKY_SPRACUJE_V1 (ústava 135.2): bloček od office a konateľov sa uloží bez zaradenia od nich — zaradí a potvrdí back office (Na určenie)
  const lenNavrh = isBlocok && !smieSpracovatBlocky(user)
  const [learnHint, setLearnHint] = useState(null)   // {total, hits} ak Matrix predvyplnil
  const form = useDirtyState({
    supplier_name: editItem?.supplier_name || '',
    supplier_ico: editItem?.supplier_ico || '',
    supplier_dic: '', supplier_iban: editItem?.supplier_iban || '',
    invoice_number: editItem?.cislo || '',
    variable_symbol: editItem?.vs || '',
    date_issued: editItem?.date_issued || '',
    date_due: editItem?.date_due || '',
    date_delivered: '',
    amount_ex_vat: '', vat_rate: '23', vat_amount: '',
    amount_total: editItem ? String(editItem.amount_total || '').replace('.', ',') : '',
    invoice_kind: editItem?.invoice_kind || '',
    cost_center: editItem?.cost_center || '',
    payment_method: editItem?.payment_method || 'karta',
    category: editItem?.cost_category || '',
    project_id: editItem?.project_id || '',
    notes: editItem?.note || ''
  })
  const updateField = (k, v) => form.setValue({ ...form.value, [k]: v })

  // onSupplierChange — pri zmene dodávateľa Matrix predvyplní typ z histórie (ústava 15.4)
  const onSupplierChange = (field, value) => {
    const next = { ...form.value, [field]: value }
    if (!isEdit && !next.invoice_kind) {   // len NEW, len ak user ešte typ nevybral
      const res = learnFromSupplier(next.supplier_ico, next.supplier_name, incoming)
      if (res && res.invoice_kind) {
        next.invoice_kind = res.invoice_kind
        next.cost_center = res.cost_center
        next.category = res.cost_category
        setLearnHint({ total: res.total, hits: res.hits })
      }
    }
    form.setValue(next)
  }
  const [phase, setPhase] = useState(editItem ? 'form' : 'start')   // start | form | confirm
  const [dirtyDialog, setDirtyDialog] = useState(false)
  const handleClose = () => { if (form.isDirty) setDirtyDialog(true); else onClose() }
  const [extracting, setExtracting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [exItems, setExItems] = useState([])   // položky faktúry z AI (KROS štýl)
  const [docFile, setDocFile] = useState('')   // V3 P1: nahratý súbor dokladu ostáva ako príloha (doc_file)

  // V3 pilot Došlé FA (131.7): súbor vybraný mimo okna (nahratie viacerých dokladov) sa hneď pošle na rozpoznanie
  const initialDone = useRef(null)
  useEffect(() => {
    if (open && initialFile && initialDone.current !== initialFile) { initialDone.current = initialFile; handleFile(initialFile) }
  }, [open, initialFile])   // eslint-disable-line react-hooks/exhaustive-deps
  async function handleFile(file) {
    if (!file) return
    setExtracting(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      if (isBlocok) fd.append('is_receipt', 'true')
      const r = await fetchAuth('/api/incoming-invoices/extract', { method: 'POST', body: fd })
      if (!r.ok) {
        const j = await r.json().catch(() => ({}))
        toast.error(`Extrakcia zlyhala: ${j.error || 'HTTP ' + r.status}`)
        return
      }
      const _resp = await r.json()
      const d = _resp.extracted || _resp   // backend vracia { extracted: {...} } — predtým sa čítalo zle (prázdny formulár)
      setDocFile(_resp.doc_file || '')
      if (_resp.noKey || _resp.ai_error) {   // V3 P1: AI neprečítala — súbor je priložený, údaje sa vyplnia ručne
        toast.error('AI doklad neprečítala' + (_resp.ai_error ? ' (' + _resp.ai_error + ')' : '') + ' — vyplň údaje ručne, súbor dokladu je priložený')
        setPhase('form')
        return
      }
      // AI položky faktúry → mapovanie na DB tvar (incoming_invoice_items)
      const aiItems = Array.isArray(d.items) ? d.items.map((it, i) => ({
        sort_order: i,
        name: it.popis || it.name || '',
        qty: it.qty != null ? it.qty : 1,
        unit: it.mj || it.unit || 'ks',
        price_unit: it.price_unit != null ? it.price_unit : null,
        price_total: it.amount_total != null ? it.amount_total : (it.price_total != null ? it.price_total : null),
        vat_rate: d.vat_rate != null ? d.vat_rate : 23,
        item_type: ''
      })) : []
      setExItems(aiItems)
      // Matrix učenie — predvyplní typ z histórie dodávateľa (ústava 15.4)
      const learned = !isEdit ? learnFromSupplier(d.supplier_ico || '', d.supplier_name || '', incoming) : null
      form.setValue({
        supplier_name: d.supplier_name || '',
        supplier_ico: d.supplier_ico || '',
        supplier_dic: d.supplier_dic || '',
        supplier_iban: d.supplier_iban || '',
        invoice_number: d.invoice_number || '',
        variable_symbol: d.variable_symbol || '',
        date_issued: d.date_issued || '',
        date_due: d.date_due || '',
        date_delivered: d.date_delivered || '',
        amount_ex_vat: d.amount_ex_vat != null ? String(d.amount_ex_vat).replace('.', ',') : '',
        vat_rate: d.vat_rate != null ? String(d.vat_rate) : '23',
        vat_amount: d.vat_amount != null ? String(d.vat_amount).replace('.', ',') : '',
        amount_total: d.amount_total != null ? String(d.amount_total).replace('.', ',') : '',
        invoice_kind: learned?.invoice_kind || '',
        cost_center: learned?.cost_center || '',
        category: learned?.cost_category || d.category || '',
        project_id: '',
        payment_method: form.value.payment_method || 'karta',   // V3 P1: rozpoznanie nesmie zmazať zvolenú platbu
        notes: d.notes || ''
      })
      if (learned?.invoice_kind) setLearnHint({ total: learned.total, hits: learned.hits })
      toast.success(learned?.invoice_kind
        ? 'AI vyplnila údaje · Matrix predvyplnil typ podľa histórie dodávateľa'
        : 'AI vyplnila údaje — skontroluj a uprav')
      setPhase('form')
    } catch (e) {
      toast.error(`Chyba extrakcie: ${e.message}`)
    } finally {
      setExtracting(false)
    }
  }

  async function doSave(force, pinAuditDup) {
    // DOSLE_RAZENIE_V1 (Mirko 24. 10. 2026): nerozumný dátum (budúcnosť alebo pred rokom 2020) — upozorni pred uložením, preklep by doklad hodil na vrch zoznamu
    const dNerozumny = (d) => !!d && (d > new Date(Date.now() + 86400000).toISOString().slice(0, 10) || d < '2020-01-01')
    const zle = [['vystavenia', form.value.date_issued], ['dodania', form.value.date_delivered]].filter(([, d]) => dNerozumny(String(d || '')))
    if (zle.length && !force && !window.confirm('Dátum ' + zle.map(([n, d]) => n + ' ' + SK.date(d)).join(' a ') + ' je v budúcnosti alebo veľmi starý — nie je to preklep?\n\nOK = uložiť aj tak · Zrušiť = opravím dátum')) return
    setSaving(true)
    try {
      const payload = {
        doc_type: docType,
        is_receipt: isBlocok ? 1 : 0,
        supplier_name: form.value.supplier_name.trim(),
        supplier_ico: form.value.supplier_ico,
        supplier_dic: form.value.supplier_dic,
        supplier_iban: form.value.supplier_iban,
        invoice_number: form.value.invoice_number,
        variable_symbol: form.value.variable_symbol,
        date_issued: form.value.date_issued,
        date_due: form.value.date_due,
        date_delivered: form.value.date_delivered,
        amount_ex_vat: SK.parse(String(form.value.amount_ex_vat)) || 0,
        vat_rate: SK.parse(String(form.value.vat_rate)) || 0,
        vat_amount: SK.parse(String(form.value.vat_amount)) || 0,
        amount_total: SK.parse(String(form.value.amount_total)) || 0,
        invoice_kind: form.value.invoice_kind,
        cost_center: form.value.cost_center || null,
        kind_confirmed: lenNavrh ? 0 : 1,   // user prešiel Form→Confirm→Potvrdiť = typ potvrdený; bloček od office potvrdí back office (135.2)
        category: form.value.category,
        cost_category: form.value.category,
        payment_method: form.value.payment_method,
        project_id: form.value.project_id || null,
        notes: form.value.notes,
        // 11.9.2026 (K2): položky sa posielajú LEN keď ich AI extrakcia naozaj dala — prázdne pole by pri úprave zmazalo existujúce položky
        ...(exItems.length ? { items: exItems } : {}),
        ...(!isEdit && docFile ? { doc_file: docFile } : {}),   // V3 P1: príloha dokladu z nahratia
        force: !!force,
        // p26: doklad na rozdelenie sa uloží bez zaradenia — časti sa určia v detaile tlačidlom Deliť
        ...(form.value.delit && !isEdit && !isBlocok ? { invoice_kind: '', cost_center: null, category: '', cost_category: '', project_id: null, kind_confirmed: 0 } : {}),
        // BLOCKY_SPRACUJE_V1: úprava bločku od office nemení zaradenie ani potvrdenie (polia sa neposielajú, server ich nechá)
        ...(lenNavrh && isEdit ? { invoice_kind: undefined, cost_center: undefined, category: undefined, cost_category: undefined, project_id: undefined, kind_confirmed: undefined } : {}),
        ...(pinAuditDup ? { pin_audit_id: pinAuditDup } : {})   // p24: schválenie presnej duplicity PIN-om overí server
      }
      const url = isEdit ? `/api/incoming-invoices/${editItem.realId}` : '/api/incoming-invoices'
      const method = isEdit ? 'PUT' : 'POST'
      const r = await fetchAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      const j = await r.json().catch(() => ({}))
      if (!isEdit && j && j.warning === 'duplicate') {
        if (window.confirm(`Možný duplikát — ${j.reason || 'podobný doklad už existuje'}.\n\nUložiť aj tak?`)) {
          // p24 (13.2 úroveň 1): presná duplicita (rovnaké číslo dokladu) sa uloží len so schválením PIN-om
          if (j.presny) {
            const gDup = await pinGate('duplicita_dokladu', { dodavatel: form.value.supplier_name, cislo_FA: form.value.invoice_number || '(nová)', dovod: j.reason || '' })
            if (!gDup || !gDup.ok) { setSaving(false); return }
            return doSave(true, gDup.audit_id)
          }
          return doSave(true)
        }
        setSaving(false)
        return
      }
      if (!r.ok) {
        // PIN výnimka — cash FA nad €400 (ústava 12.3)
        if (r.status === 400 && j.error && /Cash FA nad/i.test(j.error)) {
          setSaving(false)
          const amount = SK.parse(String(form.value.amount_total)) || 0
          const gateResult = await pinGate('cash_fa_over_400', {
            dodavatel: form.value.supplier_name,
            cislo_FA: form.value.invoice_number || '(nová)',
            suma_EUR: amount,
          })
          if (gateResult && gateResult.ok) {
            // Retry s force_cash_over_limit
            setSaving(true)
            const r2 = await fetchAuth(url, {
              method,
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...payload, force_cash_over_limit: true, pin_audit_id: gateResult.audit_id }),
            })
            const j2 = await r2.json().catch(() => ({}))
            if (!r2.ok) {
              toast.error(`Uloženie zlyhalo aj po PIN: ${j2.error || 'HTTP ' + r2.status}`)
              setSaving(false)
              return
            }
            onSaved(j2)
            return
          }
          // user zrušil PIN
          return
        }
        toast.error(`Uloženie zlyhalo: ${j.error || 'HTTP ' + r.status}`)
        setSaving(false)
        return
      }
      onSaved(j)
    } catch (e) {
      toast.error(`Chyba: ${e.message}`)
      setSaving(false)
    }
  }

  function validate() {
    if (!form.value.supplier_name.trim()) { toast.error('Dodávateľ je povinný'); return false }
    if (!form.value.amount_total || SK.parse(String(form.value.amount_total)) <= 0) {
      toast.error('Suma celkom musí byť vyplnená'); return false
    }
    if (lenNavrh) return true   // BLOCKY_SPRACUJE_V1: zaradenie bločku doplní back office (135.2)
    if (!form.value.invoice_kind && !form.value.delit) { toast.error('Vyber typ dokladu — Evidenčná alebo Nákladová, alebo zaškrtni, že doklad treba rozdeliť'); return false }
    if (!form.value.delit && form.value.invoice_kind === 'nakladova') {
      if (!form.value.cost_center) { toast.error('Vyber stredisko — Réžia alebo Zákazka'); return false }
      if (!form.value.category) { toast.error('Vyber kategóriu nákladu'); return false }
      if (form.value.cost_center === 'zakazka' && !form.value.project_id) { toast.error('Vyber zákazku'); return false }
    }
    return true
  }

  const title = isEdit ? (isBlocok ? 'Upraviť bloček' : 'Upraviť došlú faktúru') : (isBlocok ? 'Nový pokladničný bloček' : 'Nová došlá faktúra')
  const footer =
    phase === 'start' ? <BtnGhost onClick={handleClose}>Zrušiť</BtnGhost> :
    phase === 'form' ? (
      <>
        <BtnGhost onClick={handleClose}>Zrušiť</BtnGhost>
        <BtnNav onClick={() => { if (validate()) setPhase('confirm') }} disabled={isLocked}>
          {isLocked ? 'Zamknuté iným' : 'Pokračovať na potvrdenie →'}
        </BtnNav>
      </>
    ) : (
      <>
        <BtnGhost onClick={() => setPhase('form')}>← Späť na úpravy</BtnGhost>
        <BtnAdd onClick={() => doSave(false)} disabled={saving || isLocked}>
          {saving ? 'Ukladám…' : (isLocked ? 'Zamknuté' : '✓ Potvrdiť uloženie')}
        </BtnAdd>
      </>
    )

  return (
    <Modal open={open} onClose={onClose}
      title={phase === 'confirm' ? `Potvrdiť — ${title}` : title}
      subtitle={phase === 'start' ? 'Nahraj súbor — AI vyplní údaje, alebo vyplň ručne'
        : phase === 'form' ? 'Skontroluj a uprav údaje' : 'Pred potvrdením sa NIČ neuloží (ústava 12.0)'}
      footer={footer}
      isDirty={phase !== 'start' && form.isDirty}
      onDirtyClose={() => setDirtyDialog(true)}
    >
      {/* P1.2_USELOCK_2026-05-30: zámok banner ak iný user edituje */}
      {isLocked && (
        <div style={{ background: '#fef2f2', border: `1px solid #ef4444`, borderRadius: 6,
          padding: 12, marginBottom: 12, fontSize: 13, color: '#b91c1c' }}>
          Doklad práve upravuje <strong>{lockInfo.by}</strong>. Uloženie je zablokované, aby sa zmeny neprepísali.
        </div>
      )}
      {phase === 'start' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.lg, padding: SPACE.md }}>
          <label style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: SPACE.sm,
            padding: SPACE.xl, border: `2px dashed ${COLORS.nav}`, borderRadius: RADIUS.lg,
            background: COLORS.navBg, cursor: extracting ? 'wait' : 'pointer'
          }}>
            <div style={{ fontSize: 40 }}>{extracting ? '' : ''}</div>
            <div style={{ fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.bold, color: COLORS.nav }}>
              {extracting ? 'AI analyzuje doklad…' : 'Nahrať súbor — AI vyplní údaje'}
            </div>
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
              PDF, JPG, PNG · Claude Vision prečíta dodávateľa, sumy, dátumy
            </div>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" disabled={extracting}
              onChange={e => handleFile(e.target.files?.[0])}
              style={{ display: 'none' }} />
          </label>
          <div style={{ textAlign: 'center', color: COLORS.muted, fontSize: TYPO.sizes.sm }}>— alebo —</div>
          <Btn onClick={() => setPhase('form')} style={{ alignSelf: 'center' }}>Vyplniť ručne</Btn>
        </div>
      )}

      {phase === 'form' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: SPACE.md }}>
          <Input label="Dodávateľ *" value={form.value.supplier_name} onChange={v => onSupplierChange('supplier_name', v)} placeholder="napr. Hornbach SK" />
          <Input label="IČO dodávateľa" value={form.value.supplier_ico} onChange={v => onSupplierChange('supplier_ico', v)} />
          <Input label="IBAN dodávateľa" value={form.value.supplier_iban} onChange={v => updateField('supplier_iban', v)} placeholder="SK… (pre QR platbu)" />
          <Input label="Číslo dokladu" value={form.value.invoice_number} onChange={v => updateField('invoice_number', v)} />
          <Input label="Variabilný symbol" value={form.value.variable_symbol} onChange={v => updateField('variable_symbol', v)} />
          <Input label="Dátum vystavenia" type="date" value={form.value.date_issued} onChange={v => updateField('date_issued', v)} />
          <Input label="Dátum splatnosti" type="date" value={form.value.date_due} onChange={v => updateField('date_due', v)} />
          <NumberInput label="Suma bez DPH (€)" value={form.value.amount_ex_vat} onChange={v => updateField('amount_ex_vat', v)} placeholder="0,00" />
          <NumberInput label="DPH (€)" value={form.value.vat_amount} onChange={v => updateField('vat_amount', v)} placeholder="0,00" />
          <NumberInput label="Suma celkom (€) *" value={form.value.amount_total} onChange={v => updateField('amount_total', v)} placeholder="0,00" />
          {/* Matrix učenie — hint že typ bol predvyplnený z histórie (ústava 15.4) */}
          {learnHint && (
            <div style={{ gridColumn: '1 / -1', padding: SPACE.sm, background: COLORS.navBg, border: `1px solid ${COLORS.nav}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.xs, color: COLORS.text }}>
              Matrix predvyplnil typ dokladu podľa <strong>{learnHint.total}</strong> predošlých faktúr tohto dodávateľa (najčastejšie zaradenie {learnHint.hits}×). Skontroluj — ak nesedí, prepíš kliknutím.
            </div>
          )}

          {/* SPÔSOB PLATBY — určuje typ dokladu: hotovosť=pokladničný(211) · karta=interný(261110) — ústava 12.6/85 */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, marginBottom: 4, color: COLORS.text }}>
              Spôsob platby <span style={{ color: COLORS.remove }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap' }}>
              {[['karta','\uD83D\uDCB3 Karta'],['hotovosť','\uD83D\uDCB6 Hotovosť'],['bankový prevod','\uD83C\uDFE6 Prevod']].map(([v,l]) => (
                <button key={v} type="button" onClick={() => updateField('payment_method', v)}
                  style={{ padding: '8px 16px', borderRadius: RADIUS.sm, cursor: 'pointer', fontSize: TYPO.sizes.sm, fontWeight: 600,
                    border: `2px solid ${form.value.payment_method === v ? COLORS.nav : COLORS.line}`,
                    background: form.value.payment_method === v ? COLORS.navBg : COLORS.bg,
                    color: form.value.payment_method === v ? COLORS.nav : COLORS.text }}>{l}</button>
              ))}
            </div>
          </div>

          {/* BLOCKY_SPRACUJE_V1 (ústava 135.2): bloček od office a konateľov — typ, stredisko, kategóriu a zákazku určí back office */}
          {lenNavrh ? (
            <div style={{ gridColumn: '1 / -1', padding: SPACE.sm, background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm, color: COLORS.text }}>
              <b>Zaradenie</b> — {ZARADI_BACK_OFFICE} Po uložení bloček počká, kým ho Štefánia zaradí.
            </div>
          ) : (<>
          {/* TYP DOKLADU — Evidenčná / Nákladová (port starý Matrix, sek. 14) */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, marginBottom: 4, color: COLORS.text }}>
              Typ dokladu <span style={{ color: COLORS.remove }}>*</span>
            </label>
            <KindSelector value={form.value.invoice_kind} onChange={v => {
              // zmena typu používateľom → vyčistí stredisko + kategóriu + zruší learn hint
              setLearnHint(null)
              form.setValue({ ...form.value, invoice_kind: v, cost_center: '', category: '' })
            }} />
            {!isEdit && !isBlocok && (   // p37: eKasa bločky sa nedelia (Mirko 17. 9. 2026)
              <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 8, fontSize: TYPO.sizes.sm, color: COLORS.text, lineHeight: 1.4 }}>
                <input type="checkbox" checked={!!form.value.delit} onChange={e => form.setValue({ ...form.value, delit: e.target.checked })} style={{ marginTop: 3 }} />
                <span><b>Doklad treba rozdeliť</b> (napr. materiál na sklad a doprava na zákazku). Po uložení ho otvor a v Zaradení zvoľ Deliť — určíš časti a sumy.</span>
              </label>
            )}
          </div>

          {/* STREDISKO — len ak Nákladová */}
          {form.value.invoice_kind === 'nakladova' && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, marginBottom: 4, color: COLORS.text }}>
                Stredisko <span style={{ color: COLORS.remove }}>*</span>
              </label>
              <CenterSelector value={form.value.cost_center} onChange={v => {
                form.setValue({ ...form.value, cost_center: v, category: '' })
              }} />
            </div>
          )}

          {/* KATEGÓRIA — chips, podľa strediska (1 klik = rýchlosť) */}
          {form.value.invoice_kind === 'nakladova' && form.value.cost_center && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, marginBottom: 4, color: COLORS.text }}>
                Kategória nákladu <span style={{ color: COLORS.remove }}>*</span>
              </label>
              <CategoryChips
                cats={form.value.cost_center === 'rezia' ? REZIA_CATS : ZAKAZKA_CATS}
                value={form.value.category}
                onChange={v => updateField('category', v)}
              />
            </div>
          )}

          {/* ZÁKAZKA — len ak Nákladová + stredisko Zákazka */}
          {form.value.invoice_kind === 'nakladova' && form.value.cost_center === 'zakazka' && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, marginBottom: 4, color: COLORS.text }}>
                Zákazka (na ktorú doklad patrí) <span style={{ color: COLORS.remove }}>*</span>
              </label>
              <SearchableSelect
                value={form.value.project_id}
                onChange={v => updateField('project_id', v)}
                options={[...projects].sort((a,b) => (a.name||'').localeCompare(b.name||'')).map(p => ({ value: p.id, label: `${p.name} (${p.id})` }))}
                emptyLabel="— vyber zákazku —"
                placeholder="Hľadať zákazku (názov / číslo)…"
              />
            </div>
          )}

          {/* EVIDENČNÁ — info že stredisko netreba */}
          {form.value.invoice_kind === 'evidencna' && (
            <div style={{ gridColumn: '1 / -1', padding: SPACE.sm, background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
              Evidenčná faktúra — stredisko ani kategória sa nevypĺňa. Materiál sa zúčtuje cez VSZAK/VSOST v MKsoft.
            </div>
          )}
          </>)}

          <div style={{ gridColumn: '1 / -1' }}>
            <Textarea label="Poznámka" value={form.value.notes} onChange={v => updateField('notes', v)} rows={2} />
          </div>
        </div>
      )}

      {phase === 'confirm' && (() => {
        const ei = editItem || {}
        const strediskoOf = (kind, center) => kind === 'nakladova' ? (CENTER_CONFIG[center]?.label || '—') : '— evidenčná, netreba —'
        const rows = [
          { k: 'Dodávateľ',        now: form.value.supplier_name || '—',  old: ei.supplier_name || '—' },
          { k: 'IČO',              now: form.value.supplier_ico || '—',   old: ei.supplier_ico || '—' },
          { k: 'Číslo dokladu',    now: form.value.invoice_number || '—', old: ei.cislo || '—' },
          { k: 'Dátum vystavenia', now: form.value.date_issued || '—',    old: ei.date_issued || '—' },
          { k: 'Dátum splatnosti', now: form.value.date_due || '—',       old: ei.date_due || '—' },
          { k: 'Suma celkom',      now: SK.eur(SK.parse(String(form.value.amount_total)) || 0), old: SK.eur(ei.amount_total || 0) },
          { k: 'Typ dokladu',      now: KIND_CONFIG[form.value.invoice_kind]?.label || '— nevybraté —', old: KIND_CONFIG[ei.invoice_kind]?.label || '—' },
          { k: 'Stredisko',        now: strediskoOf(form.value.invoice_kind, form.value.cost_center), old: strediskoOf(ei.invoice_kind, ei.cost_center) },
          { k: 'Kategória',        now: form.value.category || '—',       old: ei.cost_category || '—' },
          { k: 'Zákazka',          now: form.value.project_id || '—',     old: ei.project_id || '—' }
        ].filter(r => !lenNavrh || !['Typ dokladu', 'Stredisko', 'Kategória', 'Zákazka'].includes(r.k))   // BLOCKY_SPRACUJE_V1: zaradenie určí back office
        const changed = isEdit ? rows.filter(r => String(r.now) !== String(r.old)).length : 0
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
            <div style={{ padding: SPACE.md, background: COLORS.addBg, border: `1px solid ${COLORS.add}`, borderRadius: RADIUS.md }}>
              <div style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold, color: COLORS.add }}>
                {isEdit
                  ? `Zmeny sa uložia k dokladu ${ei.cislo || ei.id || ''}`
                  : `${isBlocok ? 'Bloček' : 'Došlá faktúra'} vznikne s týmito údajmi`}
              </div>
              <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 2 }}>
                Pred potvrdením sa NIČ neuloží. Systém skontroluje duplicitu (ústava 13).
                {isEdit && changed > 0 && <span style={{ color: COLORS.warn, fontWeight: TYPO.weights.semibold }}> · zmenené polia: {changed}</span>}
                {isEdit && changed === 0 && <span style={{ color: COLORS.muted }}> · žiadne polia neboli zmenené</span>}
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
              {isEdit && (
                <thead>
                  <tr style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, textTransform: 'uppercase' }}>
                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Pole</th>
                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Pôvodne</th>
                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Nová hodnota</th>
                  </tr>
                </thead>
              )}
              <tbody>
                {rows.map(r => {
                  const isCh = isEdit && String(r.now) !== String(r.old)
                  return (
                    <tr key={r.k} style={{ borderBottom: `1px solid ${COLORS.lineSoft}`, background: isCh ? 'rgba(245,158,11,0.10)' : 'transparent' }}>
                      <td style={{ padding: '6px 8px', color: COLORS.muted, width: 160 }}>
                        {isCh && <span style={{ color: COLORS.warn, marginRight: 4 }}></span>}{r.k}
                      </td>
                      {isEdit && (
                        <td style={{ padding: '6px 8px', color: COLORS.muted, textDecoration: isCh ? 'line-through' : 'none' }}>{r.old}</td>
                      )}
                      <td style={{ padding: '6px 8px', fontWeight: TYPO.weights.semibold, color: isCh ? COLORS.text : COLORS.text }}>
                        {isCh && <span style={{ color: COLORS.warn, marginRight: 4 }}>→</span>}{r.now}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
      })()}
      <Modal.DirtyConfirm
        open={dirtyDialog}
        onSave={() => { setDirtyDialog(false); if (validate()) setPhase('confirm') }}
        onContinue={() => setDirtyDialog(false)}
        onDiscard={() => { form.discard(); setDirtyDialog(false); onClose() }}
      />
    </Modal>
  )
}



// ══════════════════════════════════════════════════════════════
// VÝDAVOK BEZ FA — drobný výdavok bez faktúry (POST /api/expenses)
// Ústava sek. 12: výdavok bez dokladu → PIN nadriadeného (BETA: PIN neplatí)
// ══════════════════════════════════════════════════════════════
function VydavokBezFaModal({ open, projects, onClose, onSaved }) {
  const toast = useToast()
  const form = useDirtyState({
    date: todayLocal(),
    amount: '', supplier: '', category: 'materiál', project_id: '', note: ''
  })
  const updateField = (k, v) => form.setValue({ ...form.value, [k]: v })
  const [phase, setPhase] = useState('form')
  const [dirtyDialog, setDirtyDialog] = useState(false)
  const handleClose = () => { if (form.isDirty) setDirtyDialog(true); else onClose() }
  const [saving, setSaving] = useState(false)

  async function doSave() {
    if (!form.value.supplier.trim()) { toast.error('Popis / dodávateľ je povinný'); setPhase('form'); return }
    if (!form.value.amount || SK.parse(String(form.value.amount)) <= 0) { toast.error('Suma musí byť vyplnená'); setPhase('form'); return }
    setSaving(true)
    try {
      const payload = {
        date: form.value.date,
        amount: SK.parse(String(form.value.amount)) || 0,
        supplier: form.value.supplier.trim(),
        category: form.value.category,
        doc_type: 'VYDAVOK',
        projectId: form.value.project_id || null,
        note: form.value.note
      }
      const r = await fetchAuth('/api/expenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      const j = await r.json().catch(() => ({}))
      if (r.status === 409) {
        toast.error(j.message || 'Duplikát výdavku')
        setPhase('form'); setSaving(false); return
      }
      if (!r.ok) {
        toast.error(`Uloženie zlyhalo: ${j.error || 'HTTP ' + r.status}`)
        setPhase('form'); setSaving(false); return
      }
      onSaved(j)
    } catch (e) {
      toast.error(`Chyba: ${e.message}`)
      setSaving(false)
    }
  }

  const footer = phase === 'form' ? (
    <>
      <BtnGhost onClick={handleClose}>Zrušiť</BtnGhost>
      <BtnNav onClick={() => {
        if (!form.value.supplier.trim()) { toast.error('Popis / dodávateľ je povinný'); return }
        if (!form.value.amount) { toast.error('Suma je povinná'); return }
        setPhase('confirm')
      }}>Pokračovať na potvrdenie →</BtnNav>
    </>
  ) : (
    <>
      <BtnGhost onClick={() => setPhase('form')}>← Späť</BtnGhost>
      <BtnAdd onClick={doSave} disabled={saving}>{saving ? 'Ukladám…' : '✓ Potvrdiť uloženie'}</BtnAdd>
    </>
  )

  return (
    <Modal open={open} onClose={onClose}
      title={phase === 'form' ? 'Výdavok bez faktúry' : 'Potvrdiť výdavok'}
      subtitle={phase === 'form' ? 'Drobný výdavok bez dokladu (napr. hotovostný nákup)' : 'Pred potvrdením sa NIČ neuloží (ústava 12.0)'}
      footer={footer}
      isDirty={form.isDirty}
      onDirtyClose={() => setDirtyDialog(true)}
    >
      {phase === 'form' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: SPACE.md }}>
          <Input label="Dátum" type="date" value={form.value.date} onChange={v => updateField('date', v)} />
          <NumberInput label="Suma (€) *" value={form.value.amount} onChange={v => updateField('amount', v)} placeholder="0,00" />
          <div style={{ gridColumn: '1 / -1' }}>
            <Input label="Popis / dodávateľ *" value={form.value.supplier} onChange={v => updateField('supplier', v)} placeholder="napr. Drobný materiál — železiarstvo" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, marginBottom: 4, color: COLORS.text }}>Kategória</label>
            <select value={form.value.category} onChange={e => updateField('category', e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: TYPO.sizes.base, fontFamily: 'inherit',
                border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, background: COLORS.bg, outline: 'none', boxSizing: 'border-box' }}>
              {COST_CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, marginBottom: 4, color: COLORS.text }}>Zákazka</label>
            <SearchableSelect
              value={form.value.project_id}
              onChange={v => updateField('project_id', v)}
              options={[...projects].sort((a,b) => (a.name||'').localeCompare(b.name||'')).map(p => ({ value: p.id, label: `${p.name} (${p.id})` }))}
              emptyLabel="— réžia (bez zákazky) —"
              placeholder="Hľadať zákazku…"
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Textarea label="Poznámka" value={form.value.note} onChange={v => updateField('note', v)} rows={2} />
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
          <div style={{ padding: SPACE.md, background: COLORS.addBg, border: `1px solid ${COLORS.add}`, borderRadius: RADIUS.md }}>
            <div style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold, color: COLORS.add }}>
              Výdavok bez faktúry vznikne s týmito údajmi
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
            <tbody>
              {[
                ['Dátum', form.value.date],
                ['Suma', SK.eur(SK.parse(String(form.value.amount)) || 0)],
                ['Popis / dodávateľ', form.value.supplier],
                ['Kategória', form.value.category],
                ['Zákazka', form.value.project_id || '— réžia —']
              ].map(([k, v]) => (
                <tr key={k} style={{ borderBottom: `1px solid ${COLORS.lineSoft}` }}>
                  <td style={{ padding: '6px 8px', color: COLORS.muted, width: 180 }}>{k}</td>
                  <td style={{ padding: '6px 8px', fontWeight: TYPO.weights.semibold }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal.DirtyConfirm
        open={dirtyDialog}
        onSave={() => { setDirtyDialog(false); setPhase('confirm') }}
        onContinue={() => setDirtyDialog(false)}
        onDiscard={() => { form.discard(); setDirtyDialog(false); onClose() }}
      />
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════
// Zdieľané komponenty
// ══════════════════════════════════════════════════════════════
function EmptyPane({ text }) {
  return (
    <div style={{
      background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg,
      padding: SPACE.xxl, height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', color: COLORS.muted, fontSize: TYPO.sizes.base
    }}>
      <div style={{ fontSize: 48, marginBottom: SPACE.md }}></div>
      <div>{text}</div>
    </div>
  )
}
